import React, { useState, useEffect, useRef } from 'react';
import { Modal, Tabs, Tag, Progress, Tooltip, Avatar, Select, message, DatePicker, Input } from 'antd';
import {
    CloseOutlined,
    RocketOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    FilePdfOutlined,
    PictureOutlined,
    TeamOutlined,
    AimOutlined,
    CalendarOutlined,
    DownloadOutlined,
    EyeOutlined,
    AppstoreOutlined,
    UnorderedListOutlined,
    FileTextOutlined,
    LoadingOutlined,
    CheckSquareOutlined,
    SearchOutlined,
    DownOutlined,
    RightOutlined,
    BranchesOutlined,
    MenuOutlined,
    ApartmentOutlined,
    SaveOutlined,
    EditOutlined,
    PaperClipOutlined,
    CommentOutlined,
    UploadOutlined,
    DeleteOutlined,
    PlusOutlined,
    RobotOutlined,
    QuestionCircleOutlined,
    InfoCircleOutlined,
    CheckOutlined,
    ExportOutlined,
    ShareAltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { fetchProjectById, updateProject } from './api/projectDashboardApi';
import { uploadProjectFile, getFileDownloadUrl, getFilePreviewUrl } from '../ProjectManagement/files/api/projectFilesApi';
import MilestoneCompletionModal from '../ProjectManagement/TaskDrawer/components/MilestoneCompletionModal';
import TaskDetailDrawer from '../ProjectManagement/TaskDrawer';
import ShareProjectModal from './ShareProjectModal';
import { formatName } from '@/utils/nameFormatter';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const decodeHtmlEntities = (str) => {
    if (!str || typeof str !== 'string') return '';
    let decoded = str;
    for (let i = 0; i < 3; i++) {
        if (!/&(?:lt|gt|amp|quot|#39|nbsp);/i.test(decoded)) break;
        decoded = decoded
            .replace(/&lt;/gi, '<')
            .replace(/&gt;/gi, '>')
            .replace(/&quot;/gi, '"')
            .replace(/&#39;/gi, "'")
            .replace(/&nbsp;/gi, ' ')
            .replace(/&amp;/gi, '&');
    }
    return decoded;
};

const stripHtmlTags = (str) => {
    if (!str || typeof str !== 'string') return '';
    return decodeHtmlEntities(str)
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};

const extractMemberIds = (p) => {
    if (!p) return [];
    const rawList = Array.isArray(p.members) && p.members.length > 0
        ? p.members
        : (Array.isArray(p.collaborators) ? p.collaborators : []);
    return Array.from(
        new Set(
            rawList
                .map((m) => (typeof m === 'object' && m !== null ? String(m._id || m.id || '') : String(m || '')))
                .filter((id) => Boolean(id) && id !== '[object Object]')
        )
    );
};

const DEPARTMENTS = [
    'Development',
    'Design',
    'Marketing',
    'SEO',
    'Operations',
    'Human Resource',
    'Finance',
    'Business Development',
    'Project Management Office',
];

const CATEGORIES_WITH_SUBCATEGORIES = {
    'Amazon & Ecommerce': [
        'Listing Design',
        'Store Management',
        'Amazon Store Setup',
        'Product Research',
        'PPC & Advertising',
        'A+ Content Design',
    ],
    'Website': [
        'Website Development',
        'Website Maintenance',
        'Website Redesign',
        'UI/UX Design',
        'CMS Integration',
        'E-Commerce Website',
    ],
    'Graphic Design': [
        'Logo Design',
        'Stationery Design',
        'Brochure Design',
        'Social Media Design',
        'Brand Identity',
        'Packaging Design',
    ],
    'Mobile App': [
        'iOS App Development',
        'Android App Development',
        'Cross-Platform App',
        'Mobile App Maintenance',
        'App UI/UX Design',
    ],
    'Video Animation': [
        '2D Explainer Video',
        '3D Animation Video',
        'Video Editing',
        'Motion Graphics',
        'Logo Intro Animation',
    ],
    'Marketing': [
        'Social Media Marketing',
        'SEO Optimization',
        'Content Marketing',
        'Email Marketing',
        'PPC Campaign Management',
    ],
};

const formatQuestionnaireAnswer = (answer) => {
    if (Array.isArray(answer)) return answer.length > 0 ? answer.join(', ') : 'No answer provided';
    if (answer && typeof answer === 'object') return JSON.stringify(answer, null, 2);
    if (answer === false) return 'No';
    if (answer === true) return 'Yes';
    return answer || 'No answer provided';
};

export default function ProjectDetailsModal({
    open,
    project,
    tasks = [],
    employees = [],
    currentAdmin,
    initialEditMode = false,
    onClose,
    onOpenProject,
    onEditProject,
    onRefresh,
}) {
    // Helper to check if current user has custom permission on specific modules
    const hasCustomPermission = (modules, action = 'edit') => {
        if (!currentAdmin) return false;
        const permsObj = currentAdmin.permissions || currentAdmin.customPermissions;
        if (!permsObj) return false;

        const moduleList = Array.isArray(modules) ? modules : [modules];
        return moduleList.some((mod) => {
            const perms = permsObj[mod] || permsObj[mod.toLowerCase()] || permsObj[mod.toUpperCase()] || [];
            if (Array.isArray(perms)) {
                return (
                    perms.includes(action) ||
                    perms.includes('*') ||
                    perms.includes('all') ||
                    perms.includes('manage') ||
                    perms.includes('full')
                );
            }
            if (typeof perms === 'object' && perms !== null) {
                return Boolean(perms[action] || perms.all || perms['*'] || perms.manage || perms.full);
            }
            if (typeof perms === 'boolean') {
                return perms;
            }
            return false;
        });
    };

    // Helper to check if current user is the assigned Project Manager or Project Owner
    const isAssignedProjectManagerOrOwner = (proj) => {
        if (!proj || !currentAdmin) return false;
        const userIds = [
            currentAdmin._id,
            currentAdmin.id,
            currentAdmin.employeeId?._id || currentAdmin.employeeId,
        ]
            .filter(Boolean)
            .map((id) => String(id).trim());
        const userEmail = String(currentAdmin.email || '').trim().toLowerCase();

        const pmIds = [
            proj.projectManager?._id,
            proj.projectManager?.id,
            typeof proj.projectManager === 'string' ? proj.projectManager : null,
            proj.pm?._id,
            proj.pm?.id,
            typeof proj.pm === 'string' ? proj.pm : null,
        ]
            .filter(Boolean)
            .map((id) => String(id).trim());

        const ownerIds = [
            proj.owner?._id,
            proj.owner?.id,
            typeof proj.owner === 'string' ? proj.owner : null,
            proj.createdBy?._id,
            proj.createdBy?.id,
            typeof proj.createdBy === 'string' ? proj.createdBy : null,
        ]
            .filter(Boolean)
            .map((id) => String(id).trim());

        const allAssignedIds = [...pmIds, ...ownerIds];
        if (userIds.some((uid) => allAssignedIds.includes(uid))) return true;

        const assignedEmails = [
            proj.projectManager?.email,
            proj.pm?.email,
            proj.pmEmail,
            proj.owner?.email,
            proj.ownerEmail,
            proj.createdBy?.email,
        ]
            .filter(Boolean)
            .map((e) => String(e).trim().toLowerCase());

        if (userEmail && assignedEmails.includes(userEmail)) return true;
        return false;
    };

    // Helper to check if user can edit this project:
    // 1. super_admin, admin
    // 2. User with custom edit permissions on Projects
    // 3. Assigned Project Manager or Project Owner (even if their role is employee)
    const canEditThisProject = (proj) => {
        if (!proj || !currentAdmin) return false;
        const role = (currentAdmin.role || currentAdmin.adminRole || '').toLowerCase();
        const isSuperAdmin = ['super_admin', 'superadmin', 'admin'].includes(role);
        if (isSuperAdmin) return true;

        // Custom permission check (e.g. employee with custom edit permissions granted on Projects)
        if (hasCustomPermission(['Projects', 'ProjectDashboard', 'Project', 'All'], 'edit')) return true;

        // Project assignment check (assigned PM or Project Owner/Creator can edit everything)
        return isAssignedProjectManagerOrOwner(proj);
    };

    // Helper to check if user can edit or complete milestones:
    // 1. super_admin, admin, project manager, or manager role
    // 2. User with custom edit permissions on Milestones or Projects
    // 3. Assigned Project Manager or Project Owner (even if their role is employee)
    const canManageMilestone = (proj) => {
        if (!proj || !currentAdmin) return false;
        const role = (currentAdmin.role || currentAdmin.adminRole || '').toLowerCase();
        if (['super_admin', 'superadmin', 'admin', 'project manager', 'manager'].includes(role)) return true;

        // Custom permission check (e.g. employee with custom edit permissions granted on Projects/Milestones)
        if (hasCustomPermission(['Milestones', 'Milestone', 'Projects', 'ProjectDashboard', 'Project', 'All'], 'edit')) return true;

        // Project assignment check (assigned PM or Project Owner/Creator can edit everything)
        return isAssignedProjectManagerOrOwner(proj);
    };

    const [activeTab, setActiveTab] = useState('overview');
    const [taskSortBy, setTaskSortBy] = useState('default');
    const [assetViewMode, setAssetViewMode] = useState('grid'); // 'grid' | 'list'
    const [fullProject, setFullProject] = useState(null);
    const [fetchedTasks, setFetchedTasks] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [updatingMilestoneIndex, setUpdatingMilestoneIndex] = useState(null);
    const [completingMilestone, setCompletingMilestone] = useState(null);
    const [completingMilestoneIndex, setCompletingMilestoneIndex] = useState(null);
    const [completionModalOpen, setCompletionModalOpen] = useState(false);
    const [completingLoading, setCompletingLoading] = useState(false);
    const [questionnaireAnswersOpen, setQuestionnaireAnswersOpen] = useState(false);
    const [shareModalOpen, setShareModalOpen] = useState(false);

    // ── Inline Project Editing State ──
    const [isEditingProject, setIsEditingProject] = useState(Boolean(initialEditMode));
    const [editForm, setEditForm] = useState({});
    const [isDirty, setIsDirty] = useState(false);
    const [savingProject, setSavingProject] = useState(false);
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const [uploadingAsset, setUploadingAsset] = useState(false);
    const docInputRef = useRef(null);
    const assetInputRef = useRef(null);

    // Helper to extract goals from project (combines smartGoals, legacy goal string, and objectives array)
    const extractProjectSmartGoals = (p) => {
        if (!p) return [];
        if (Array.isArray(p.smartGoals) && p.smartGoals.length > 0) {
            return p.smartGoals.map((g, i) => ({
                id: g.id || g._id || `g-${i}`,
                name: g.name || g.title || `Goal ${i + 1}`,
                description: g.description || '',
                successCriteria: g.successCriteria || 'Completed',
                targetDate: g.targetDate || (p.dueDate ? dayjs(p.dueDate).format('YYYY-MM-DD') : ''),
                priority: g.priority || 'Medium',
            }));
        }
        const extracted = [];
        const defaultTarget = p.dueDate ? dayjs(p.dueDate).format('YYYY-MM-DD') : '';
        if (Array.isArray(p.objectives) && p.objectives.length > 0) {
            p.objectives.forEach((obj, i) => {
                if (typeof obj === 'string' && obj.trim()) {
                    const parts = obj.split(':');
                    extracted.push({
                        id: `g-obj-${i}`,
                        name: parts[0]?.trim() || `Goal ${i + 1}`,
                        description: obj.trim(),
                        successCriteria: parts.slice(1).join(':').trim() || 'Completed',
                        targetDate: defaultTarget,
                        priority: 'Medium',
                    });
                } else if (obj && typeof obj === 'object') {
                    extracted.push({
                        id: obj.id || obj._id || `g-obj-${i}`,
                        name: obj.name || obj.title || `Goal ${i + 1}`,
                        description: obj.description || '',
                        successCriteria: obj.successCriteria || 'Completed',
                        targetDate: obj.targetDate || defaultTarget,
                        priority: obj.priority || 'Medium',
                    });
                }
            });
        }
        if (p.goal && typeof p.goal === 'string' && p.goal.trim()) {
            const goalParts = p.goal.split('|').map((s) => s.trim()).filter(Boolean);
            goalParts.forEach((gStr, i) => {
                const alreadyExists = extracted.some(
                    (e) => e.name.toLowerCase() === gStr.toLowerCase() || e.description.toLowerCase() === gStr.toLowerCase()
                );
                if (!alreadyExists) {
                    extracted.unshift({
                        id: `g-main-${i}`,
                        name: gStr,
                        description: gStr,
                        successCriteria: 'Completed',
                        targetDate: defaultTarget,
                        priority: 'High',
                    });
                }
            });
        }
        return extracted;
    };

    useEffect(() => {
        if (open) {
            setIsEditingProject(Boolean(initialEditMode));
            const p = fullProject || project;
            if (p) {
                setEditForm({
                    name: p.name || '',
                    status: p.status || 'In Progress',
                    health: p.health || 'On Track',
                    priority: p.priority || 'Medium',
                    category: p.category || p.projectType || 'General',
                    subCategory: p.subCategory || p.projectSubType || '',
                    durationMode: p.durationMode || 'Fixed',
                    department: p.department || 'Development',
                    createdBy: p.createdBy?._id || p.createdBy || p.owner?._id || p.owner || '',
                    projectManager: p.projectManager?._id || p.projectManager || p.pm?._id || p.pm || '',
                    members: extractMemberIds(p),
                    startDate: p.startDate ? dayjs(p.startDate) : null,
                    dueDate: p.dueDate ? dayjs(p.dueDate) : null,
                    description: decodeHtmlEntities(p.description || ''),
                    smartGoals: extractProjectSmartGoals(p),
                });
                setIsDirty(false);
            }
        }
    }, [open, initialEditMode]);

    const handleFieldChange = (field, value) => {
        setEditForm((prev) => ({ ...prev, [field]: value }));
        setIsDirty(true);
    };

    const handleRequestClose = () => {
        if (isDirty) {
            Modal.confirm({
                title: 'Unsaved Changes',
                content: 'You have unsaved changes to this project. Do you want to discard them and close?',
                okText: 'Discard & Close',
                okType: 'danger',
                cancelText: 'Keep Editing',
                onOk: () => {
                    setIsDirty(false);
                    setIsEditingProject(false);
                    onClose();
                },
            });
            return;
        }
        setIsEditingProject(false);
        onClose();
    };

    const handleDiscardAndExitEdit = () => {
        if (isDirty) {
            Modal.confirm({
                title: 'Discard Changes',
                content: 'Are you sure you want to discard your edits?',
                okText: 'Discard',
                okType: 'danger',
                cancelText: 'Keep Editing',
                onOk: () => {
                    handleDiscardChanges();
                    setIsEditingProject(false);
                },
            });
            return;
        }
        handleDiscardChanges();
        setIsEditingProject(false);
    };

    const handleSaveProjectChanges = async () => {
        const currentProj = fullProject || project;
        if (!currentProj?._id) return;
        setSavingProject(true);
        try {
            const payload = {
                name: editForm.name,
                status: editForm.status,
                health: editForm.health,
                priority: editForm.priority,
                category: editForm.category,
                subCategory: editForm.subCategory,
                durationMode: editForm.durationMode,
                department: editForm.department,
                createdBy: editForm.createdBy || null,
                owner: editForm.createdBy || null,
                projectManager: editForm.projectManager || null,
                members: Array.isArray(editForm.members) ? editForm.members : extractMemberIds(currentProj),
                startDate: editForm.startDate ? (dayjs.isDayjs(editForm.startDate) ? editForm.startDate.toISOString() : editForm.startDate) : null,
                dueDate: editForm.durationMode === 'Ongoing' ? null : (editForm.dueDate ? (dayjs.isDayjs(editForm.dueDate) ? editForm.dueDate.toISOString() : editForm.dueDate) : null),
                description: decodeHtmlEntities(editForm.description || ''),
                smartGoals: editForm.smartGoals || [],
                goal: (editForm.smartGoals || []).map((g) => g.name).join(' | '),
                objectives: (editForm.smartGoals || []).map((g) => `${g.name}: ${g.successCriteria || 'Completed'}`),
            };
            const { data } = await updateProject(currentProj._id, payload);
            if (data?.result) {
                setFullProject(data.result);
                setIsDirty(false);
                setIsEditingProject(false);
                message.success('Project details saved successfully!');
                onRefresh?.();
            }
        } catch (err) {
            console.error('Failed to update project:', err);
            message.error(err.response?.data?.message || 'Failed to save project changes.');
        } finally {
            setSavingProject(false);
        }
    };

    const handleDiscardChanges = () => {
        const p = fullProject || project;
        if (p) {
            setEditForm({
                name: p.name || '',
                status: p.status || 'In Progress',
                health: p.health || 'On Track',
                priority: p.priority || 'Medium',
                category: p.category || p.projectType || 'General',
                subCategory: p.subCategory || p.projectSubType || '',
                durationMode: p.durationMode || 'Fixed',
                department: p.department || 'Development',
                createdBy: p.createdBy?._id || p.createdBy || p.owner?._id || p.owner || '',
                projectManager: p.projectManager?._id || p.projectManager || p.pm?._id || p.pm || '',
                members: extractMemberIds(p),
                startDate: p.startDate ? dayjs(p.startDate) : null,
                dueDate: p.dueDate ? dayjs(p.dueDate) : null,
                description: decodeHtmlEntities(p.description || ''),
                smartGoals: extractProjectSmartGoals(p),
            });
            setIsDirty(false);
        }
    };

    // ── Smart Goals State & Handlers ──
    const [showGoalModal, setShowGoalModal] = useState(false);
    const [editingGoalId, setEditingGoalId] = useState(null);
    const [goalForm, setGoalForm] = useState({ name: '', description: '', successCriteria: '', targetDate: '', priority: 'Medium' });
    const [aiGeneratingGoals, setAiGeneratingGoals] = useState(false);
    const [aiConflictModal, setAiConflictModal] = useState(false);

    const getCurrentGoalsList = (formState = editForm) => (
        formState.smartGoals !== undefined
            ? formState.smartGoals
            : extractProjectSmartGoals(fullProject || project)
    );

    const openAddGoal = () => {
        setEditingGoalId(null);
        const currentList = getCurrentGoalsList(editForm);
        const nextNum = currentList.length + 1;
        const projectDueDate = (editForm.dueDate ? (dayjs.isDayjs(editForm.dueDate) ? editForm.dueDate.format('YYYY-MM-DD') : editForm.dueDate) : '') || ((fullProject || project)?.dueDate ? dayjs((fullProject || project).dueDate).format('YYYY-MM-DD') : '');
        setGoalForm({
            name: `Goal ${nextNum} — `,
            description: '',
            successCriteria: '',
            targetDate: projectDueDate,
            priority: 'Medium',
        });
        setShowGoalModal(true);
    };

    const openEditGoal = (goal) => {
        setEditingGoalId(goal.id || goal._id);
        setGoalForm({
            name: goal.name || '',
            description: goal.description || '',
            successCriteria: goal.successCriteria || '',
            targetDate: goal.targetDate || '',
            priority: goal.priority || 'Medium',
        });
        setShowGoalModal(true);
    };

    const handleSaveGoal = () => {
        if (!goalForm.name.trim()) {
            message.warning('Goal Name is required.');
            return;
        }
        if (!goalForm.description?.trim() && !goalForm.successCriteria?.trim()) {
            message.warning('Goal Description or Success Criteria is required.');
            return;
        }
        const defaultDate = goalForm.targetDate || (editForm.dueDate ? (dayjs.isDayjs(editForm.dueDate) ? editForm.dueDate.format('YYYY-MM-DD') : editForm.dueDate) : '') || dayjs().add(30, 'day').format('YYYY-MM-DD');
        const defaultCriteria = goalForm.successCriteria?.trim() || goalForm.description?.trim() || 'Achieve goal objectives with 100% compliance';

        const updatedGoal = {
            ...goalForm,
            targetDate: defaultDate,
            successCriteria: defaultCriteria,
        };

        if (editingGoalId) {
            setEditForm((prev) => {
                const baseGoals = getCurrentGoalsList(prev);
                return {
                    ...prev,
                    smartGoals: baseGoals.map((g) => ((g.id === editingGoalId || g._id === editingGoalId) ? { ...g, ...updatedGoal } : g)),
                };
            });
            setIsDirty(true);
            message.success('Smart Goal updated.');
        } else {
            const newGoal = {
                id: `goal-${Date.now()}`,
                ...updatedGoal,
            };
            setEditForm((prev) => {
                const baseGoals = getCurrentGoalsList(prev);
                return {
                    ...prev,
                    smartGoals: [...baseGoals, newGoal],
                };
            });
            setIsDirty(true);
            message.success('Smart Goal added.');
        }
        setShowGoalModal(false);
        setEditingGoalId(null);
        setGoalForm({ name: '', description: '', successCriteria: '', targetDate: '', priority: 'Medium' });
    };

    const handleRemoveGoal = (goalId) => {
        setEditForm((prev) => {
            const baseGoals = getCurrentGoalsList(prev);
            return {
                ...prev,
                smartGoals: baseGoals.filter((g) => (g.id !== goalId && g._id !== goalId)),
            };
        });
        setIsDirty(true);
        message.success('Smart Goal removed.');
    };

    const triggerSmartGoalsGeneration = (modeOrAppend = false) => {
        const shouldAppend = modeOrAppend === true || modeOrAppend === 'append';
        setAiConflictModal(false);
        setAiGeneratingGoals(true);
        setTimeout(() => {
            const currentProj = fullProject || project;
            const currentName = editForm.name || currentProj?.name || 'Project';
            const currentDept = editForm.department || currentProj?.department || 'the team';
            const projectDueDate = (editForm.dueDate ? (dayjs.isDayjs(editForm.dueDate) ? editForm.dueDate.format('YYYY-MM-DD') : editForm.dueDate) : '') || (currentProj?.dueDate ? dayjs(currentProj.dueDate).format('YYYY-MM-DD') : dayjs().add(30, 'day').format('YYYY-MM-DD'));
            const currentGoals = getCurrentGoalsList(editForm);
            const startNum = shouldAppend ? currentGoals.length + 1 : 1;

            const generated = [
                {
                    id: `goal-${Date.now()}-1`,
                    name: `Goal ${startNum}: Automate ${currentName} Workflows`,
                    description: `Streamline operational efficiency and reduce manual processing time for ${currentDept}.`,
                    successCriteria: 'Achieve 95%+ operational automation with minimal error rates',
                    targetDate: projectDueDate,
                    priority: 'High',
                },
                {
                    id: `goal-${Date.now()}-2`,
                    name: `Goal ${startNum + 1}: Ensure High Quality & Accuracy for ${currentName} Deliverables`,
                    description: 'Establish rigorous testing, QA benchmarks, and stakeholder review loops.',
                    successCriteria: '100% compliance with performance benchmarks and client satisfaction',
                    targetDate: projectDueDate,
                    priority: 'Medium',
                },
                {
                    id: `goal-${Date.now()}-3`,
                    name: `Goal ${startNum + 2}: Enhance Reporting & Executive Visibility`,
                    description: 'Provide executive dashboards, milestone progress tracking, and audit logs.',
                    successCriteria: 'Real-time metrics available to department managers and stakeholders',
                    targetDate: editForm.startDate && dayjs(editForm.startDate).add(14, 'day').isBefore(dayjs(projectDueDate))
                        ? dayjs(editForm.startDate).add(14, 'day').format('YYYY-MM-DD')
                        : projectDueDate,
                    priority: 'Medium',
                },
            ];

            const nextGoals = shouldAppend ? [...currentGoals, ...generated] : generated;
            setEditForm((prev) => ({ ...prev, smartGoals: nextGoals }));
            setIsDirty(true);
            setAiGeneratingGoals(false);
            message.success(shouldAppend ? `AI appended Goal ${startNum} through Goal ${startNum + 2}!` : 'AI generated 3 S.M.A.R.T. Goals!');
        }, 1000);
    };

    const handleGenerateSmartGoals = () => {
        if (getCurrentGoalsList(editForm).length > 0) {
            setAiConflictModal(true);
        } else {
            triggerSmartGoalsGeneration(false);
        }
    };

    // ── Milestone Management State & Handlers ──
    const [showMilestoneModal, setShowMilestoneModal] = useState(false);
    const [editingMilestone, setEditingMilestone] = useState(null);
    const [savingMilestone, setSavingMilestone] = useState(false);
    const [milestoneForm, setMilestoneForm] = useState({
        name: '',
        description: '',
        startDate: '',
        dueDate: '',
        completionCriteria: '',
        owner: '',
        status: 'Pending',
        deliverables: [],
        deliverableChecklist: [],
    });
    const [newDeliverableTitle, setNewDeliverableTitle] = useState('');

    const openAddMilestone = () => {
        setEditingMilestone(null);
        const currentProj = fullProject || project;
        const nextNum = (currentProj?.milestones || []).length + 1;
        setMilestoneForm({
            name: `Milestone ${nextNum} — `,
            description: '',
            startDate: currentProj?.startDate ? dayjs(currentProj.startDate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
            dueDate: currentProj?.dueDate ? dayjs(currentProj.dueDate).format('YYYY-MM-DD') : dayjs().add(14, 'day').format('YYYY-MM-DD'),
            completionCriteria: '',
            owner: currentProj?.projectManager?._id || currentProj?.projectManager || currentProj?.owner?._id || currentProj?.owner || '',
            status: 'Pending',
            deliverables: [],
            deliverableChecklist: [],
        });
        setNewDeliverableTitle('');
        setShowMilestoneModal(true);
    };

    const openEditMilestone = (m) => {
        setEditingMilestone(m);
        const checklist = (Array.isArray(m.deliverableChecklist) && m.deliverableChecklist.length > 0)
            ? m.deliverableChecklist
            : (Array.isArray(m.deliverables)
                ? m.deliverables.map((d, idx) => ({
                    deliverableId: d.deliverableId || d.id || `DELIV-${idx + 1}`,
                    title: d.title || d.name || 'Deliverable',
                    completed: Boolean(d.completed || d.done),
                    rejected: Boolean(d.rejected),
                    rejectionNote: d.rejectionNote || '',
                    order: d.order !== undefined ? d.order : idx + 1,
                }))
                : []);

        setMilestoneForm({
            name: m.name || m.title || '',
            description: m.description || '',
            startDate: m.startDate ? dayjs(m.startDate).format('YYYY-MM-DD') : '',
            dueDate: m.dueDate ? dayjs(m.dueDate).format('YYYY-MM-DD') : '',
            completionCriteria: m.completionCriteria || '',
            owner: m.owner?._id || m.owner || '',
            status: m.status || 'Pending',
            deliverables: Array.isArray(m.deliverables) ? m.deliverables : [],
            deliverableChecklist: checklist,
        });
        setNewDeliverableTitle('');
        setShowMilestoneModal(true);
    };

    const handleAddDeliverableToForm = () => {
        if (!newDeliverableTitle.trim()) return;
        const newItem = {
            deliverableId: `deliv-${Date.now()}`,
            title: newDeliverableTitle.trim(),
            name: newDeliverableTitle.trim(),
            completed: false,
            done: false,
            order: (milestoneForm.deliverableChecklist || []).length + 1,
        };
        setMilestoneForm((prev) => ({
            ...prev,
            deliverables: [...(prev.deliverables || []), newItem],
            deliverableChecklist: [...(prev.deliverableChecklist || []), newItem],
        }));
        setNewDeliverableTitle('');
    };

    const handleRemoveDeliverableFromForm = (index) => {
        setMilestoneForm((prev) => ({
            ...prev,
            deliverables: (prev.deliverables || []).filter((_, i) => i !== index),
            deliverableChecklist: (prev.deliverableChecklist || []).filter((_, i) => i !== index),
        }));
    };

    const handleToggleDeliverableInForm = (index) => {
        setMilestoneForm((prev) => {
            const nextList = [...(prev.deliverableChecklist || [])];
            if (!nextList[index]) return prev;
            const nextVal = !nextList[index].completed;
            nextList[index] = {
                ...nextList[index],
                completed: nextVal,
                done: nextVal,
                completedAt: nextVal ? new Date().toISOString() : null,
            };
            const nextDels = (prev.deliverables || []).map((d, i) => (i === index ? { ...d, done: nextVal, completed: nextVal } : d));
            return {
                ...prev,
                deliverableChecklist: nextList,
                deliverables: nextDels,
            };
        });
    };

    const handleSaveMilestone = async () => {
        if (!milestoneForm.name.trim()) {
            message.warning('Milestone Name is required.');
            return;
        }
        const currentProj = fullProject || project;
        if (!currentProj?._id) return;
        setSavingMilestone(true);
        try {
            let updatedMilestones = [...(currentProj.milestones || [])];
            const newOrUpdatedItem = {
                name: milestoneForm.name.trim(),
                title: milestoneForm.name.trim(),
                description: milestoneForm.description || '',
                startDate: milestoneForm.startDate || null,
                dueDate: milestoneForm.dueDate || null,
                completionCriteria: milestoneForm.completionCriteria || '',
                owner: milestoneForm.owner || null,
                status: milestoneForm.status || 'Pending',
                deliverables: milestoneForm.deliverables || [],
                deliverableChecklist: milestoneForm.deliverableChecklist || [],
            };

            if (editingMilestone) {
                const targetId = String(editingMilestone._id || editingMilestone.id);
                updatedMilestones = updatedMilestones.map((m) => {
                    const mId = String(m._id || m.id);
                    if (mId === targetId || m === editingMilestone) {
                        return { ...m, ...newOrUpdatedItem, _id: m._id || m.id };
                    }
                    return m;
                });
            } else {
                updatedMilestones.push({
                    ...newOrUpdatedItem,
                    id: `m-${Date.now()}`,
                });
            }

            const { data } = await updateProject(currentProj._id, { milestones: updatedMilestones });
            if (data?.result) {
                setFullProject(data.result);
                message.success(editingMilestone ? 'Milestone updated successfully!' : 'Milestone added successfully!');
                setShowMilestoneModal(false);
                setEditingMilestone(null);
                onRefresh?.();
            }
        } catch (err) {
            console.error('Failed to save milestone:', err);
            message.error(err?.response?.data?.message || 'Failed to save milestone.');
        } finally {
            setSavingMilestone(false);
        }
    };

    const handleDeleteMilestone = (m, mIndex) => {
        const currentProj = fullProject || project;
        if (!currentProj?._id) return;
        if (!canManageMilestone(currentProj)) {
            message.warning('Only the Project Manager or Admin can delete milestones.');
            return;
        }
        Modal.confirm({
            title: 'Delete Milestone',
            content: `Are you sure you want to delete "${m.name || m.title || `Milestone ${mIndex + 1}`}"? Any tasks linked to this milestone will become unassigned.`,
            okText: 'Delete',
            okType: 'danger',
            onOk: async () => {
                try {
                    const targetId = String(m._id || m.id);
                    const updatedMilestones = (currentProj.milestones || []).filter((item, idx) => {
                        const itemId = String(item._id || item.id);
                        return itemId ? itemId !== targetId : idx !== mIndex;
                    });
                    const { data } = await updateProject(currentProj._id, { milestones: updatedMilestones });
                    if (data?.result) {
                        setFullProject(data.result);
                        message.success('Milestone removed.');
                        onRefresh?.();
                    }
                } catch (err) {
                    console.error('Failed to delete milestone:', err);
                    message.error('Failed to delete milestone.');
                }
            },
        });
    };

    const handleViewDocument = async (doc, idx) => {
        if (doc.publicUrl && !doc.publicUrl.startsWith('blob:')) {
            window.open(doc.publicUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (doc.fileUrl && !doc.fileUrl.startsWith('blob:')) {
            window.open(doc.fileUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        const currentProj = fullProject || project;
        const fileId = doc._id || doc.id || doc.documentId || (currentProj?._id ? `doc-${currentProj._id}-${idx}` : null);
        if (!fileId) {
            message.warning('File reference not available for preview.');
            return;
        }
        const hide = message.loading('Generating secure preview link...', 0);
        try {
            const res = await getFilePreviewUrl(fileId);
            if (res?.previewUrl || res?.downloadUrl) {
                window.open(res.previewUrl || res.downloadUrl, '_blank', 'noopener,noreferrer');
            } else {
                message.error('Preview link could not be generated.');
            }
        } catch (err) {
            console.error('Failed to preview document:', err);
            message.error(err.response?.data?.message || 'Failed to generate preview URL.');
        } finally {
            hide();
        }
    };

    const handleDownloadDocument = async (doc, idx) => {
        if (doc.publicUrl && !doc.publicUrl.startsWith('blob:')) {
            const a = document.createElement('a');
            a.href = doc.publicUrl;
            a.download = doc.fileName || 'document';
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            return;
        }
        const currentProj = fullProject || project;
        const fileId = doc._id || doc.id || doc.documentId || (currentProj?._id ? `doc-${currentProj._id}-${idx}` : null);
        if (!fileId) {
            message.warning('File reference not available for download.');
            return;
        }
        const hide = message.loading('Generating secure download link...', 0);
        try {
            const res = await getFileDownloadUrl(fileId);
            if (res?.downloadUrl) {
                const a = document.createElement('a');
                a.href = res.downloadUrl;
                a.download = res.fileName || doc.fileName || 'download';
                a.target = '_blank';
                a.rel = 'noopener noreferrer';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            } else {
                message.error('Download link could not be generated.');
            }
        } catch (err) {
            console.error('Failed to download document:', err);
            message.error(err.response?.data?.message || 'Failed to generate download URL.');
        } finally {
            hide();
        }
    };

    const handleViewAsset = async (asset, idx) => {
        if (asset.publicUrl && !asset.publicUrl.startsWith('blob:')) {
            window.open(asset.publicUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        if (asset.fileUrl && !asset.fileUrl.startsWith('blob:')) {
            window.open(asset.fileUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        const currentProj = fullProject || project;
        const fileId = asset._id || asset.id || (currentProj?._id ? `asset-${currentProj._id}-${idx}` : null);
        if (!fileId) {
            message.warning('Asset reference not available for preview.');
            return;
        }
        const hide = message.loading('Generating secure preview link...', 0);
        try {
            const res = await getFilePreviewUrl(fileId);
            if (res?.previewUrl || res?.downloadUrl) {
                window.open(res.previewUrl || res.downloadUrl, '_blank', 'noopener,noreferrer');
            } else {
                message.error('Preview link could not be generated.');
            }
        } catch (err) {
            console.error('Failed to preview asset:', err);
            message.error(err.response?.data?.message || 'Failed to generate preview URL.');
        } finally {
            hide();
        }
    };

    const handleDownloadAsset = async (asset, idx) => {
        if (asset.publicUrl && !asset.publicUrl.startsWith('blob:')) {
            const a = document.createElement('a');
            a.href = asset.publicUrl;
            a.download = asset.fileName || 'asset';
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            return;
        }
        const currentProj = fullProject || project;
        const fileId = asset._id || asset.id || (currentProj?._id ? `asset-${currentProj._id}-${idx}` : null);
        if (!fileId) {
            message.warning('Asset reference not available for download.');
            return;
        }
        const hide = message.loading('Generating secure download link...', 0);
        try {
            const res = await getFileDownloadUrl(fileId);
            if (res?.downloadUrl) {
                const a = document.createElement('a');
                a.href = res.downloadUrl;
                a.download = res.fileName || asset.fileName || 'asset';
                a.target = '_blank';
                a.rel = 'noopener noreferrer';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            } else {
                message.error('Download link could not be generated.');
            }
        } catch (err) {
            console.error('Failed to download asset:', err);
            message.error(err.response?.data?.message || 'Failed to generate download URL.');
        } finally {
            hide();
        }
    };

    const handleUploadDocument = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const currentProj = fullProject || project;
        if (!currentProj?._id) return;
        setUploadingDoc(true);
        const hide = message.loading(`Uploading ${file.name}...`, 0);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('projectId', currentProj._id);
            formData.append('category', 'Document');
            formData.append('description', 'Uploaded from Project Details modal');

            const res = await uploadProjectFile(formData);
            const d = res?.data || res;
            const newDoc = {
                _id: d.id || d._id || undefined,
                id: d.id || d._id || undefined,
                documentId: d.id || d._id || String(Date.now()),
                fileName: d.originalName || file.name,
                fileSize: d.size || file.size,
                fileType: d.type || file.name.split('.').pop().toUpperCase(),
                publicUrl: d.publicUrl || d.fileUrl || d.url || '',
                storageKey: d.storageKey || '',
                uploadedAt: new Date().toISOString(),
                uploadedBy: currentAdmin?.name || 'User',
            };

            const updatedDocs = [...(currentProj.documents || []), newDoc];
            const { data } = await updateProject(currentProj._id, { documents: updatedDocs });
            if (data?.result) {
                setFullProject(data.result);
                message.success(`Document "${file.name}" uploaded successfully!`);
                onRefresh?.();
            }
        } catch (err) {
            console.error('Failed to upload document:', err);
            message.error('Failed to upload document.');
        } finally {
            hide();
            setUploadingDoc(false);
            if (docInputRef.current) docInputRef.current.value = '';
        }
    };

    const handleDeleteDocument = async (docIndex) => {
        const currentProj = fullProject || project;
        if (!canEditThisProject(currentProj)) return;
        Modal.confirm({
            title: 'Delete Document',
            content: 'Are you sure you want to remove this document from the project?',
            okText: 'Delete',
            okType: 'danger',
            onOk: async () => {
                try {
                    const updatedDocs = (currentProj.documents || []).filter((_, idx) => idx !== docIndex);
                    const { data } = await updateProject(currentProj._id, { documents: updatedDocs });
                    if (data?.result) {
                        setFullProject(data.result);
                        message.success('Document removed successfully.');
                        onRefresh?.();
                    }
                } catch (err) {
                    console.error('Failed to delete document:', err);
                    message.error('Failed to delete document.');
                }
            },
        });
    };

    const handleUploadAsset = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const currentProj = fullProject || project;
        if (!currentProj?._id) return;
        setUploadingAsset(true);
        const hide = message.loading(`Uploading asset ${file.name}...`, 0);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('projectId', currentProj._id);
            formData.append('category', 'Asset');
            formData.append('description', 'Uploaded from Project Details modal');

            const res = await uploadProjectFile(formData);
            const d = res?.data || res;
            const newAsset = {
                _id: d.id || d._id || undefined,
                id: d.id || d._id || undefined,
                fileName: d.originalName || file.name,
                fileSize: d.size || file.size,
                fileType: d.type || file.name.split('.').pop().toUpperCase(),
                publicUrl: d.publicUrl || d.fileUrl || d.url || '',
                storageKey: d.storageKey || '',
                uploadedAt: new Date().toISOString(),
                uploadedBy: currentAdmin?.name || 'User',
            };

            const updatedAssets = [...(currentProj.assets || []), newAsset];
            const { data } = await updateProject(currentProj._id, { assets: updatedAssets });
            if (data?.result) {
                setFullProject(data.result);
                message.success(`Asset "${file.name}" uploaded successfully!`);
                onRefresh?.();
            }
        } catch (err) {
            console.error('Failed to upload asset:', err);
            message.error('Failed to upload asset.');
        } finally {
            hide();
            setUploadingAsset(false);
            if (assetInputRef.current) assetInputRef.current.value = '';
        }
    };

    const handleDeleteAsset = async (assetIndex) => {
        const currentProj = fullProject || project;
        if (!canEditThisProject(currentProj)) return;
        Modal.confirm({
            title: 'Delete Asset',
            content: 'Are you sure you want to remove this asset from the project?',
            okText: 'Delete',
            okType: 'danger',
            onOk: async () => {
                try {
                    const updatedAssets = (currentProj.assets || []).filter((_, idx) => idx !== assetIndex);
                    const { data } = await updateProject(currentProj._id, { assets: updatedAssets });
                    if (data?.result) {
                        setFullProject(data.result);
                        message.success('Asset removed successfully.');
                        onRefresh?.();
                    }
                } catch (err) {
                    console.error('Failed to delete asset:', err);
                    message.error('Failed to delete asset.');
                }
            },
        });
    };

    const formatTaskDate = (task) => {
        if (!task) return '—';
        if (task.startDate && task.dueDate) {
            const s = dayjs(task.startDate);
            const d = dayjs(task.dueDate);
            if (s.isSame(d, 'month')) {
                return `${s.format('MMM D')} - ${d.format('D')}`;
            }
            return `${s.format('MMM D')} - ${d.format('MMM D')}`;
        }
        if (task.dueDate) return dayjs(task.dueDate).format('MMM D');
        if (task.startDate) return dayjs(task.startDate).format('MMM D');
        return '—';
    };

    // ── Task Detail Drawer State ──
    const [drawerTaskId, setDrawerTaskId] = useState(null);
    const [drawerMode, setDrawerMode] = useState('view');
    const [drawerMilestoneId, setDrawerMilestoneId] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const openTaskInDrawer = (taskId) => {
        if (!taskId) return;
        setDrawerMode('view');
        setDrawerMilestoneId(null);
        setDrawerTaskId(String(taskId));
        setIsDrawerOpen(true);
    };

    const closeTaskDrawer = () => {
        setIsDrawerOpen(false);
        setDrawerTaskId(null);
        setDrawerMilestoneId(null);
        setDrawerMode('view');
    };

    const openCreateTaskInDrawer = (targetMilestoneId = null) => {
        setDrawerMode('create');
        setDrawerMilestoneId(targetMilestoneId ? String(targetMilestoneId) : null);
        setDrawerTaskId(null);
        setIsDrawerOpen(true);
    };

    const handleTaskUpdatedFromDrawer = (updatedTask) => {
        if (!updatedTask?._id) return;
        setFetchedTasks((prev) =>
            prev.map((t) => (String(t._id || t.id) === String(updatedTask._id) ? { ...t, ...updatedTask } : t))
        );
    };

    const handleTaskCreatedFromDrawer = async () => {
        const currentProj = fullProject || project;
        if (currentProj?._id) {
            try {
                const { data } = await axios.get('/project-management/tasks', { params: { projectId: currentProj._id, limit: 'all' } });
                if (data?.result) {
                    setFetchedTasks(data.result);
                }
            } catch (err) {
                console.error('Failed to reload tasks:', err);
            }
        }
        onRefresh?.();
    };

    // ── Task Tab State & Filters ──
    const [taskSearch, setTaskSearch] = useState('');
    const [taskStatusFilter, setTaskStatusFilter] = useState('all');
    const [taskAssigneeFilter, setTaskAssigneeFilter] = useState('all');
    const [taskPage, setTaskPage] = useState(1);
    const [taskPageSize, setTaskPageSize] = useState(15);
    const [taskTypeFilter, setTaskTypeFilter] = useState('all'); // 'all' | 'parents' | 'subtasks'
    const [taskViewMode, setTaskViewMode] = useState('hierarchy'); // 'hierarchy' | 'tree' | 'flat'
    const [expandedViewTaskIds, setExpandedViewTaskIds] = useState(new Set());
    const [expandedMilestoneTaskIds, setExpandedMilestoneTaskIds] = useState(new Set());
    const [collapsedMilestoneIds, setCollapsedMilestoneIds] = useState(new Set());

    const toggleMilestoneCollapse = (mKey) => {
        setCollapsedMilestoneIds((prev) => {
            const next = new Set(prev);
            const key = String(mKey);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleViewTaskExpand = (taskId) => {
        setExpandedViewTaskIds((prev) => {
            const next = new Set(prev);
            const key = String(taskId);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleMilestoneTaskExpand = (taskId) => {
        setExpandedMilestoneTaskIds((prev) => {
            const next = new Set(prev);
            const key = String(taskId);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleMilestoneStatusChange = async (mIndex, newStatus, options = {}) => {
        const currentProject = fullProject || project;
        if (!currentProject?._id) return;
        if (!canManageMilestone(currentProject)) {
            message.warning('Only the Project Manager or Admin can edit or complete milestones.');
            return;
        }
        const targetMilestone = (currentProject.milestones || [])[mIndex];

        // Intercept Completed status to ensure deliverables checklist verification gate
        if (newStatus === 'Completed' && !options.skipModal) {
            const allTasks = fetchedTasks.length > 0 ? fetchedTasks : tasks;
            const targetMsId = String(targetMilestone?._id || targetMilestone?.id || '');
            const targetMsName = targetMilestone?.name || targetMilestone?.title || '';
            const mTasks = (allTasks || []).filter((t) => {
                const tProjId = String(t.project?._id || t.project || '');
                if (tProjId !== String(currentProject._id)) return false;
                const tMsId = t.milestoneId ? (typeof t.milestoneId === 'object' ? (t.milestoneId._id || t.milestoneId.id) : t.milestoneId) : (t.milestone ? (typeof t.milestone === 'object' ? t.milestone._id : t.milestone) : null);
                if (tMsId && String(tMsId) === targetMsId) return true;
                if (t.milestoneName && t.milestoneName === targetMsName) return true;
                return false;
            });
            const totalChildTasks = mTasks.length;
            const completedChildTasks = mTasks.filter((t) => t.status === 'Completed' || t.status === 'Closed' || t.status === 'Close').length;

            setCompletingMilestone({
                ...targetMilestone,
                name: targetMilestone?.name || targetMilestone?.title || `Milestone ${mIndex + 1}`,
                title: targetMilestone?.title || targetMilestone?.name || `Milestone ${mIndex + 1}`,
                projectName: currentProject.name || 'Project',
                project: currentProject,
                totalChildTasks,
                completedChildTasks,
                remainingChildTasks: Math.max(0, totalChildTasks - completedChildTasks),
            });
            setCompletingMilestoneIndex(mIndex);
            setCompletionModalOpen(true);
            return;
        }

        setUpdatingMilestoneIndex(mIndex);
        try {
            const updatedMilestones = (currentProject.milestones || []).map((m, i) => {
                const nameVal = m.name || m.title || '';
                const milestoneId = m._id || m.id;
                if (i === mIndex) {
                    const nextM = { ...m, _id: milestoneId, name: nameVal, title: nameVal, status: newStatus };
                    if (options.completionComments) {
                        nextM.completionComments = options.completionComments;
                    }
                    if (options.deliverableChecklist) {
                        nextM.deliverableChecklist = options.deliverableChecklist;
                        if (Array.isArray(m.deliverables)) {
                            nextM.deliverables = m.deliverables.map((del, dIdx) => {
                                const matched = options.deliverableChecklist.find((c) =>
                                    (c.deliverableId && (c.deliverableId === del.id || c.deliverableId === del.deliverableId)) ||
                                    (c.title && (c.title === del.title || c.title === del.name)) ||
                                    (c.order !== undefined && c.order === (dIdx + 1))
                                );
                                return {
                                    ...del,
                                    done: matched ? Boolean(matched.completed) : true,
                                    completed: matched ? Boolean(matched.completed) : true,
                                };
                            });
                        }
                    }
                    return nextM;
                }
                return { ...m, _id: milestoneId, name: nameVal, title: nameVal };
            });

            const { data } = await updateProject(currentProject._id, { milestones: updatedMilestones });
            if (data?.result) {
                setFullProject(data.result);
                message.success(`Milestone updated to "${newStatus}"!`);
                onRefresh?.();
            }
        } catch (err) {
            console.error('Failed to update milestone status:', err);
            message.error('Failed to update milestone status.');
        } finally {
            setUpdatingMilestoneIndex(null);
        }
    };

    const handleConfirmMilestoneCompletion = async (comments, verifiedChecklist) => {
        if (completingMilestoneIndex === null || completingMilestoneIndex === undefined) return;
        setCompletingLoading(true);
        try {
            await handleMilestoneStatusChange(completingMilestoneIndex, 'Completed', {
                skipModal: true,
                completionComments: comments,
                deliverableChecklist: verifiedChecklist,
            });
            setCompletionModalOpen(false);
            setCompletingMilestone(null);
            setCompletingMilestoneIndex(null);
        } catch (err) {
            console.error('Failed to complete milestone:', err);
            message.error('Failed to complete milestone.');
        } finally {
            setCompletingLoading(false);
        }
    };

    // Re-fetch whenever modal opens OR the underlying project data changes (e.g. after edit/save)
    const projectFingerprint = project ? `${project._id}-${project.updatedAt || ''}-${(project.activityLogs || []).length}` : '';
    useEffect(() => {
        if (open && project?._id) {
            setFullProject(project);
            setLoadingDetails(true);
            Promise.allSettled([
                fetchProjectById(project._id),
                axios.get('/project-management/tasks', { params: { projectId: project._id, limit: 'all' } })
            ]).then(([projRes, tasksRes]) => {
                if (projRes.status === 'fulfilled' && projRes.value?.data?.result) {
                    const fetchedProj = projRes.value.data.result;
                    setFullProject(fetchedProj);
                    setEditForm((prev) => {
                        if (isDirty) return prev;
                        return {
                            name: fetchedProj.name || '',
                            status: fetchedProj.status || 'In Progress',
                            health: fetchedProj.health || 'On Track',
                            priority: fetchedProj.priority || 'Medium',
                            category: fetchedProj.category || fetchedProj.projectType || 'General',
                            subCategory: fetchedProj.subCategory || fetchedProj.projectSubType || '',
                            durationMode: fetchedProj.durationMode || 'Fixed',
                            department: fetchedProj.department || 'Development',
                            createdBy: fetchedProj.createdBy?._id || fetchedProj.createdBy || fetchedProj.owner?._id || fetchedProj.owner || '',
                            projectManager: fetchedProj.projectManager?._id || fetchedProj.projectManager || fetchedProj.pm?._id || fetchedProj.pm || '',
                            members: extractMemberIds(fetchedProj),
                            startDate: fetchedProj.startDate ? dayjs(fetchedProj.startDate) : null,
                            dueDate: fetchedProj.dueDate ? dayjs(fetchedProj.dueDate) : null,
                            description: decodeHtmlEntities(fetchedProj.description || ''),
                            smartGoals: extractProjectSmartGoals(fetchedProj),
                        };
                    });
                }
                if (tasksRes.status === 'fulfilled' && tasksRes.value?.data?.result) {
                    setFetchedTasks(tasksRes.value.data.result);
                }
            }).catch((err) => console.error('Failed to fetch full project details:', err))
                .finally(() => setLoadingDetails(false));
        } else {
            setFullProject(null);
            setFetchedTasks([]);
        }
    }, [open, projectFingerprint]);

    if (!project) return null;

    const activeProject = fullProject || project;

    // ── Computed Project Progress & Metrics ──────────────────────────────────
    const combinedTasks = fetchedTasks.length > 0 ? fetchedTasks : tasks;
    const projectTasks = combinedTasks.filter((t) => String(t.project?._id || t.project) === String(activeProject._id));
    const isMilestoneTask = (t) => {
        const itemType = String(t.itemType || '').toLowerCase();
        const taskCode = String(t.taskId || t.code || '').toUpperCase();
        return itemType === 'milestone' || taskCode.startsWith('MLS-') || Boolean(t.isNativeTaskMilestone);
    };
    const regularTasks = projectTasks.filter((t) => !isMilestoneTask(t));
    const taskMilestones = projectTasks.filter((t) => isMilestoneTask(t));

    const completedTasks = regularTasks.filter((t) => t.status === 'Completed' || t.status === 'Closed' || t.status === 'Close').length;
    const taskProgressPct = regularTasks.length > 0
        ? Math.round((completedTasks / regularTasks.length) * 100)
        : (activeProject.progress !== undefined ? activeProject.progress : 0);

    // Combine project milestones with native task milestones
    const existingMilestones = activeProject.milestones || [];
    const extraTaskMilestones = taskMilestones
        .filter((tm) => !existingMilestones.some((em) => String(em._id || em.id) === String(tm._id) || (em.name || em.title) === tm.title))
        .map((tm) => ({
            _id: tm._id,
            name: tm.title,
            title: tm.title,
            status: tm.status,
            dueDate: tm.dueDate,
            completionCriteria: tm.completionCriteria,
            deliverables: tm.deliverables,
            isNativeTaskMilestone: true,
        }));

    const milestones = [...existingMilestones, ...extraTaskMilestones];
    const completedMilestones = milestones.filter((m) => m.status === 'Completed' || m.status === 'Closed' || m.status === 'Close').length;
    const milestoneProgressPct = milestones.length > 0 ? Math.round((completedMilestones / milestones.length) * 100) : 0;

    const overallProgressPct = milestones.length > 0
        ? Math.round(taskProgressPct * 0.6 + milestoneProgressPct * 0.4)
        : taskProgressPct;

    // ── Task Metrics & Filtering (Tasks Tab) ─────────────────────────────────
    const taskById = new Map();
    const subtasksByParentId = new Map();
    regularTasks.forEach((t) => {
        const id = String(t._id || t.id);
        taskById.set(id, t);
        if (t.parentId) {
            const pId = String(typeof t.parentId === 'object' ? (t.parentId._id || t.parentId.id) : t.parentId);
            if (!subtasksByParentId.has(pId)) subtasksByParentId.set(pId, []);
            subtasksByParentId.get(pId).push(t);
        }
    });

    const allParentTasks = regularTasks.filter((t) => !t.parentId);
    const allSubtasks = regularTasks.filter((t) => Boolean(t.parentId));
    const assignedTasksCount = regularTasks.filter((t) => Boolean(t.assignee || t.assignedTo)).length;
    const unassignedTasksCount = regularTasks.length - assignedTasksCount;

    // Helper to evaluate filter match on a single task
    const doesTaskMatchFilters = (t) => {
        const term = taskSearch.toLowerCase().trim();
        if (term) {
            const title = (t.name || t.title || '').toLowerCase();
            const code = (t.taskId || '').toLowerCase();
            const assigneeObj = t.assignedTo || t.assignee;
            const assigneeName = (typeof assigneeObj === 'object' && assigneeObj?.name
                ? `${assigneeObj.name} ${assigneeObj.surname || ''}`
                : (employees.find((e) => String(e._id) === String(assigneeObj))?.name || (typeof assigneeObj === 'string' ? assigneeObj : ''))).toLowerCase();
            if (!title.includes(term) && !code.includes(term) && !assigneeName.includes(term)) return false;
        }
        const normTaskStatus = (t.status === 'Closed' || t.status === 'Close') ? 'Completed' : t.status;
        if (taskStatusFilter !== 'all' && normTaskStatus !== taskStatusFilter) return false;
        const hasAssignee = Boolean(t.assignee || t.assignedTo);
        if (taskAssigneeFilter === 'assigned' && !hasAssignee) return false;
        if (taskAssigneeFilter === 'unassigned' && hasAssignee) return false;
        return true;
    };

    // ── Task Sorting Helpers ──
    const getAssigneeSortName = (task) => {
        const a = task.assignedTo || task.assignee;
        if (typeof a === 'object' && a !== null) {
            return `${a.name || ''} ${a.surname || ''}`.trim().toLowerCase() || 'zzz';
        }
        const emp = employees.find((e) => String(e._id) === String(a));
        if (emp) return `${emp.name || ''} ${emp.surname || ''}`.trim().toLowerCase();
        if (typeof a === 'string' && a) return a.toLowerCase();
        return 'zzz';
    };

    const getTaskDueDateTimestamp = (task) => {
        const d = task.dueDate || task.timeline?.dueDate || task.endDate;
        return d ? new Date(d).getTime() : 0;
    };

    const PRIORITY_WEIGHT = { 'Critical': 4, 'High': 3, 'Medium': 2, 'Low': 1 };

    const sortTaskList = (list) => {
        if (!Array.isArray(list) || list.length <= 1 || taskSortBy === 'default') return list;
        const sorted = [...list];
        sorted.sort((a, b) => {
            if (taskSortBy === 'due_asc') {
                const ta = getTaskDueDateTimestamp(a);
                const tb = getTaskDueDateTimestamp(b);
                if (!ta && !tb) return 0;
                if (!ta) return 1;
                if (!tb) return -1;
                return ta - tb;
            }
            if (taskSortBy === 'due_desc') {
                const ta = getTaskDueDateTimestamp(a);
                const tb = getTaskDueDateTimestamp(b);
                if (!ta && !tb) return 0;
                if (!ta) return 1;
                if (!tb) return -1;
                return tb - ta;
            }
            if (taskSortBy === 'assignee_asc') {
                return getAssigneeSortName(a).localeCompare(getAssigneeSortName(b));
            }
            if (taskSortBy === 'assignee_desc') {
                return getAssigneeSortName(b).localeCompare(getAssigneeSortName(a));
            }
            if (taskSortBy === 'priority_desc') {
                const pa = PRIORITY_WEIGHT[a.priority] || 0;
                const pb = PRIORITY_WEIGHT[b.priority] || 0;
                return pb - pa;
            }
            if (taskSortBy === 'priority_asc') {
                const pa = PRIORITY_WEIGHT[a.priority] || 0;
                const pb = PRIORITY_WEIGHT[b.priority] || 0;
                return pa - pb;
            }
            if (taskSortBy === 'name_asc') {
                return (a.name || a.title || '').localeCompare(b.name || b.title || '');
            }
            if (taskSortBy === 'name_desc') {
                return (b.name || b.title || '').localeCompare(a.name || a.title || '');
            }
            return 0;
        });
        return sorted;
    };

    // Helper to check if task belongs to a milestone
    const isTaskInMilestone = (task, m) => {
        const targetMsId = String(m._id || m.id || '');
        const targetMsName = m.name || m.title || '';
        const tMsId = task.milestoneId ? (typeof task.milestoneId === 'object' ? (task.milestoneId._id || task.milestoneId.id) : task.milestoneId) : (task.milestone ? (typeof task.milestone === 'object' ? task.milestone._id : task.milestone) : null);
        if (tMsId && String(tMsId) === targetMsId) return true;
        if (tMsId && targetMsName && String(tMsId) === String(targetMsName)) return true;
        if (task.milestoneName && (task.milestoneName === targetMsName)) return true;
        return false;
    };

    // Milestones and their grouped parent tasks for Hierarchy View
    const milestoneHierarchyGroups = milestones.map((m, mIdx) => {
        const mKey = String(m._id || m.id || `m_${mIdx}`);
        const mParentTasks = allParentTasks.filter((parent) => isTaskInMilestone(parent, m));
        const filteredParents = mParentTasks.filter((parent) => {
            const parentMatches = doesTaskMatchFilters(parent);
            const children = subtasksByParentId.get(String(parent._id || parent.id)) || [];
            const hasMatchingChild = children.some((c) => doesTaskMatchFilters(c));
            return parentMatches || hasMatchingChild;
        });
        const allMTasks = regularTasks.filter((t) => isTaskInMilestone(t, m));
        const completedMTasks = allMTasks.filter((t) => t.status === 'Completed' || t.status === 'Closed' || t.status === 'Close').length;
        return {
            milestone: m,
            mKey,
            mIndex: mIdx,
            parentTasks: filteredParents,
            totalTasksCount: allMTasks.length,
            completedTasksCount: completedMTasks,
        };
    });

    // Unassigned parent tasks (tasks not in any milestone)
    const unassignedParentTasks = allParentTasks.filter((parent) => {
        const hasMilestone = milestones.some((m) => isTaskInMilestone(parent, m));
        if (hasMilestone) return false;
        const parentMatches = doesTaskMatchFilters(parent);
        const children = subtasksByParentId.get(String(parent._id || parent.id)) || [];
        const hasMatchingChild = children.some((c) => doesTaskMatchFilters(c));
        return parentMatches || hasMatchingChild;
    });

    const expandAllHierarchy = () => {
        setCollapsedMilestoneIds(new Set());
        setExpandedViewTaskIds(new Set(allParentTasks.map((p) => String(p._id || p.id))));
    };

    const collapseAllHierarchy = () => {
        const allKeys = new Set(milestones.map((m, i) => String(m._id || m.id || `m_${i}`)));
        allKeys.add('unassigned');
        setCollapsedMilestoneIds(allKeys);
        setExpandedViewTaskIds(new Set());
    };

    // Tree View: Parent tasks filtering (matches if parent matches or any child subtask matches)
    const rawTreeParents = allParentTasks.filter((parent) => {
        const parentMatches = doesTaskMatchFilters(parent);
        const children = subtasksByParentId.get(String(parent._id || parent.id)) || [];
        const hasMatchingChild = children.some((c) => doesTaskMatchFilters(c));
        return parentMatches || hasMatchingChild;
    });

    const filteredTreeParents = sortTaskList(rawTreeParents);

    // Orphan subtasks (subtasks whose parentId is not among known project tasks)
    const orphanSubtasks = allSubtasks.filter((st) => {
        const pId = String(typeof st.parentId === 'object' ? (st.parentId._id || st.parentId.id) : st.parentId);
        return !taskById.has(pId) && doesTaskMatchFilters(st);
    });

    // Flat View: All tasks filtering
    const rawFlatTasks = regularTasks.filter((t) => {
        if (!doesTaskMatchFilters(t)) return false;
        if (taskTypeFilter === 'parents' && t.parentId) return false;
        if (taskTypeFilter === 'subtasks' && !t.parentId) return false;
        return true;
    });

    const filteredFlatTasks = sortTaskList(rawFlatTasks);

    const isTree = taskViewMode === 'tree';
    const activeListLength = isTree ? filteredTreeParents.length : filteredFlatTasks.length;
    const totalTaskPages = taskPageSize === 9999 ? 1 : (Math.ceil(activeListLength / taskPageSize) || 1);
    const paginatedTreeParents = taskPageSize === 9999 ? filteredTreeParents : filteredTreeParents.slice((taskPage - 1) * taskPageSize, (taskPage - 1) * taskPageSize + taskPageSize);
    const paginatedFlatTasks = taskPageSize === 9999 ? filteredFlatTasks : filteredFlatTasks.slice((taskPage - 1) * taskPageSize, (taskPage - 1) * taskPageSize + taskPageSize);

    const expandAllTree = () => {
        setExpandedViewTaskIds(new Set(allParentTasks.map((p) => String(p._id || p.id))));
    };
    const collapseAllTree = () => {
        setExpandedViewTaskIds(new Set());
    };

    // ── Resolved Team Members ────────────────────────────────────────────────
    const getOwnerName = () => {
        let name = '';
        if (activeProject.createdBy?.name) {
            name = `${activeProject.createdBy.name} ${activeProject.createdBy.surname || ''}`.trim();
        } else {
            const emp = employees.find((e) => String(e._id) === String(activeProject.createdBy));
            if (emp?.name) name = emp.name;
            else name = activeProject.supervisor || 'Unassigned';
        }
        return formatName(name) || name;
    };

    const getManagerName = () => {
        let name = '';
        if (activeProject.projectManager?.name) {
            name = `${activeProject.projectManager.name} ${activeProject.projectManager.surname || ''}`.trim();
        } else {
            const emp = employees.find((e) => String(e._id) === String(activeProject.projectManager));
            name = emp?.name || 'Unassigned';
        }
        return formatName(name) || name;
    };

    const resolveMemberEntry = (m) => {
        if (!m) return null;
        const mId = typeof m === 'object' && m !== null ? String(m._id || m.id || '') : String(m || '');
        if (typeof m === 'object' && m !== null && (m.name || m.firstName || m.fullName)) {
            const fullName = m.fullName || `${m.name || m.firstName || ''} ${m.surname || m.lastName || ''}`.trim();
            return {
                ...m,
                _id: mId || m.email || fullName,
                name: formatName(fullName) || fullName,
                department: m.department || 'Collaborator',
            };
        }
        if (!mId || mId === '[object Object]') return null;
        const emp = employees.find((e) => String(e._id || e.id) === mId);
        if (emp) {
            const fullName = emp.fullName || `${emp.name || emp.firstName || ''} ${emp.surname || emp.lastName || ''}`.trim();
            return {
                ...emp,
                _id: String(emp._id || emp.id),
                name: formatName(fullName) || fullName,
                department: emp.department || emp.role || 'Collaborator',
            };
        }
        return null;
    };

    const collaborators = (() => {
        const sourceMembers = isEditingProject && Array.isArray(editForm.members)
            ? editForm.members
            : (Array.isArray(activeProject.members) && activeProject.members.length > 0
                ? activeProject.members
                : (Array.isArray(activeProject.collaborators) ? activeProject.collaborators : []));

        const seenIds = new Set();
        const list = [];

        const addEntry = (raw) => {
            const resolved = resolveMemberEntry(raw);
            if (!resolved) return;
            const key = String(resolved._id || resolved.name);
            if (seenIds.has(key)) return;
            seenIds.add(key);
            list.push(resolved);
        };

        sourceMembers.forEach(addEntry);

        // If viewing and explicit members didn't include task assignees, also surface active task assignees
        if (!isEditingProject) {
            regularTasks.forEach((t) => {
                const assignee = t.assignedTo || t.assignee;
                if (assignee) addEntry(assignee);
            });
        }

        return list;
    })();

    const documents = activeProject.documents || [];
    const assets = activeProject.assets || [];
    const questionnaireResponses = Array.isArray(activeProject.linkedQuestionnaire?.responses)
        ? [...activeProject.linkedQuestionnaire.responses].sort((a, b) => (a.order || 0) - (b.order || 0))
        : [];
    const proposalValue = activeProject.proposal;
    const proposalUrl = typeof proposalValue === 'string' && /^https?:\/\//i.test(proposalValue) ? proposalValue : '';
    const proposalTitle = typeof proposalValue === 'string' && !/^https?:\/\//i.test(proposalValue)
        ? proposalValue
        : (proposalUrl ? 'Client Proposal' : '');

    // Health Badge helper
    const renderHealthBadge = (health) => {
        if (health === 'Critical') {
            return <Tag color="error" className="font-bold text-xs px-2.5 py-0.5 rounded-full flex items-center gap-1"><WarningOutlined /> Critical</Tag>;
        }
        if (health === 'At Risk') {
            return <Tag color="warning" className="font-bold text-xs px-2.5 py-0.5 rounded-full flex items-center gap-1"><WarningOutlined /> At Risk</Tag>;
        }
        return <Tag color="success" className="font-bold text-xs px-2.5 py-0.5 rounded-full flex items-center gap-1"><CheckCircleOutlined /> On Track</Tag>;
    };

    const getActivityLogs = () => {
        const rawLogs = [];
        if (Array.isArray(activeProject.activityLogs) && activeProject.activityLogs.length > 0) {
            rawLogs.push(...activeProject.activityLogs);
        }

        if (rawLogs.length === 0) {
            if (activeProject.creProjectId || activeProject.pcrNumber) {
                rawLogs.push({
                    action: 'CRE Project Intake Approved & Activated',
                    description: `CRE Intake Request (${activeProject.pcrNumber || activeProject.creProjectId}) was approved and activated in ERP.`,
                    performedBy: getOwnerName() || 'System',
                    timestamp: activeProject.createdAt || activeProject.startDate || new Date(),
                    type: 'intake',
                });
            } else {
                rawLogs.push({
                    action: 'Project Created in ERP',
                    description: `Project "${activeProject.name}" (${activeProject.projectCode || 'PRJ'}) was created.`,
                    performedBy: getOwnerName() || 'System',
                    timestamp: activeProject.createdAt || activeProject.startDate || new Date(),
                    type: 'creation',
                });
            }

            (activeProject.milestones || []).forEach((m, idx) => {
                const msTitle = m.name || m.title || `Milestone ${idx + 1}`;
                if (m.status === 'Completed') {
                    rawLogs.push({
                        action: `Milestone Completed: ${msTitle}`,
                        description: `Milestone "${msTitle}" marked as Completed in ERP.`,
                        performedBy: 'PM / System',
                        timestamp: m.updatedAt || activeProject.updatedAt || new Date(),
                        type: 'milestone',
                    });
                }
                if (m.notes) {
                    if (m.notes.includes('[BD Review')) {
                        rawLogs.push({
                            action: `BD Milestone Review Submitted: ${msTitle}`,
                            description: `BD reviewed milestone "${msTitle}" and submitted to client.`,
                            performedBy: 'BD Owner',
                            timestamp: m.updatedAt || activeProject.updatedAt || new Date(),
                            type: 'milestone',
                        });
                    }
                    if (m.notes.includes('[Client Confirmation]')) {
                        rawLogs.push({
                            action: `Client Milestone Confirmed & Payment Released: ${msTitle}`,
                            description: `Client confirmed completion and released payment for milestone "${msTitle}".`,
                            performedBy: 'Client',
                            timestamp: m.updatedAt || activeProject.updatedAt || new Date(),
                            type: 'cre_sync',
                        });
                    }
                }
            });
        }

        // Deduplicate identical actions within same minute
        const seenKeys = new Set();
        const uniqueLogs = [];

        for (const log of rawLogs) {
            const timeKey = log.timestamp ? dayjs(log.timestamp).format('YYYY-MM-DD-HH:mm') : 'now';
            const dedupeKey = `${log.action}|${log.description}|${timeKey}`;
            if (!seenKeys.has(dedupeKey)) {
                seenKeys.add(dedupeKey);
                uniqueLogs.push(log);
            }
        }

        return uniqueLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    };

    return (
        <>
            <Modal
                open={open}
                onCancel={handleRequestClose}
                footer={null}
                width={1140}
                destroyOnClose
                centered
                closable={false}
                styles={{ content: { padding: 0, borderRadius: 16, overflow: 'hidden' }, body: { padding: 0 } }}
            >
                <div className="bg-white dark:bg-[#111827] flex flex-col max-h-[90vh] overflow-hidden text-slate-800 dark:text-slate-200 font-sans">

                    {/* ── Modal Header (Sticky Header with Key Status + Actions) ── */}
                    <div className="p-6 border-b border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/60 flex flex-col gap-4">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="space-y-1">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <span className="w-3.5 h-3.5 rounded-full shrink-0" style={{ backgroundColor: activeProject.color || '#03488a' }} />
                                    {isEditingProject && canEditThisProject(activeProject) ? (
                                        <input
                                            type="text"
                                            value={editForm.name !== undefined ? editForm.name : (activeProject.name || '')}
                                            onChange={(e) => handleFieldChange('name', e.target.value)}
                                            className="text-xl font-bold text-[#003263] dark:text-[#a7c8ff] tracking-tight bg-white dark:bg-slate-800 border border-blue-500 rounded-lg px-2.5 py-1 outline-none transition-all shadow-xs min-w-[240px]"
                                            placeholder="Enter project name..."
                                            title="Edit project name"
                                        />
                                    ) : (
                                        <h2 className="text-xl font-bold text-[#003263] dark:text-[#a7c8ff] tracking-tight">{activeProject.name}</h2>
                                    )}
                                    <span className="font-mono text-xs font-bold px-2 py-0.5 bg-slate-200 dark:bg-slate-800 rounded-md text-slate-600 dark:text-slate-300">
                                        {activeProject.projectCode || 'PRJ-001'}
                                    </span>
                                    <Tag color="cyan" className="font-bold text-xs px-2.5 py-0.5 rounded-md mr-0">
                                        {activeProject.brandName || (activeProject.brandId?.includes('e360') ? 'e360Digital' : activeProject.brandId?.includes('cport') || activeProject.brandId?.includes('cellular') ? 'CellularPort' : 'DymaxTech')}
                                    </Tag>
                                    <Tag color={activeProject.isClientProject ? 'blue' : 'purple'} className="font-bold text-xs px-2.5 py-0.5 rounded-md mr-0">
                                        {activeProject.isClientProject ? 'Client' : 'In-house'}
                                    </Tag>
                                    {loadingDetails && <LoadingOutlined className="text-blue-600 text-sm" />}
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium pl-6">
                                    {activeProject.department || 'General'} • Created {dayjs(activeProject.createdAt).format('MMM DD, YYYY')}
                                </p>
                            </div>

                            {/* Top Actions */}
                            <div className="flex items-center gap-2.5">
                                {canEditThisProject(activeProject) && (
                                    isEditingProject ? (
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={handleDiscardAndExitEdit}
                                                disabled={savingProject}
                                                className="px-3.5 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-200/80 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-xl transition-all"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleSaveProjectChanges}
                                                disabled={savingProject}
                                                className="px-4 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-md transition-all flex items-center gap-1.5 active:scale-95 disabled:opacity-50"
                                            >
                                                {savingProject ? <LoadingOutlined /> : <SaveOutlined />} Save Changes
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                handleDiscardChanges();
                                                setIsEditingProject(true);
                                            }}
                                            className="px-3.5 py-2 text-xs font-bold text-amber-700 bg-amber-100 hover:bg-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:hover:bg-amber-900/60 rounded-xl transition-all flex items-center gap-1.5 shadow-xs active:scale-95"
                                            title="Edit project details"
                                        >
                                            <EditOutlined /> Edit Project
                                        </button>
                                    )
                                )}
                                <button
                                    type="button"
                                    onClick={() => {
                                        const projId = project?._id || activeProject?._id;
                                        if (projId) {
                                            window.open(`/project-dashboard?projectId=${projId}&tab=overview`, '_blank');
                                        } else {
                                            onOpenProject(project);
                                        }
                                    }}
                                    className="px-4 py-2 text-xs font-bold text-white rounded-xl shadow-md hover:opacity-90 active:scale-95 transition-all flex items-center gap-1.5"
                                    style={{ background: 'linear-gradient(90deg, #03488A 0%, #006591 100%)' }}
                                >
                                    <ExportOutlined /> View Full Detail
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShareModalOpen(true)}
                                    className="px-3.5 py-2 text-xs font-bold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-all flex items-center gap-1.5 shadow-xs active:scale-95"
                                    title="Share project"
                                >
                                    <ShareAltOutlined /> Share Project
                                </button>
                                <button onClick={handleRequestClose} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors">
                                    <CloseOutlined className="text-slate-600 dark:text-slate-400 text-lg" />
                                </button>
                            </div>
                        </div>

                        {/* Header Key Metrics Bar */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
                            <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Status</span>
                                {isEditingProject && canEditThisProject(activeProject) ? (
                                    <Select
                                        size="small"
                                        value={editForm.status || activeProject.status || 'In Progress'}
                                        onChange={(val) => handleFieldChange('status', val)}
                                        className="font-bold text-xs min-w-[125px]"
                                    >
                                        <Select.Option value="Draft"><Tag color="default">Draft</Tag></Select.Option>
                                        <Select.Option value="Planning"><Tag color="cyan">Planning</Tag></Select.Option>
                                        <Select.Option value="In Progress"><Tag color="blue">In Progress</Tag></Select.Option>
                                        <Select.Option value="Active"><Tag color="blue">Active</Tag></Select.Option>
                                        <Select.Option value="Under Review"><Tag color="purple">Under Review</Tag></Select.Option>
                                        <Select.Option value="Completed"><Tag color="green">Completed</Tag></Select.Option>
                                        <Select.Option value="On Hold"><Tag color="orange">On Hold</Tag></Select.Option>
                                        <Select.Option value="Cancelled"><Tag color="red">Cancelled</Tag></Select.Option>
                                    </Select>
                                ) : (
                                    <Tag color="blue" className="font-bold text-xs mr-0">{activeProject.status || 'In Progress'}</Tag>
                                )}
                            </div>
                            <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                <span className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Health</span>
                                {isEditingProject && canEditThisProject(activeProject) ? (
                                    <Select
                                        size="small"
                                        value={editForm.health || activeProject.health || 'On Track'}
                                        onChange={(val) => handleFieldChange('health', val)}
                                        className="font-bold text-xs min-w-[115px]"
                                    >
                                        <Select.Option value="On Track"><Tag color="success"><CheckCircleOutlined /> On Track</Tag></Select.Option>
                                        <Select.Option value="At Risk"><Tag color="warning"><WarningOutlined /> At Risk</Tag></Select.Option>
                                        <Select.Option value="Critical"><Tag color="error"><WarningOutlined /> Critical</Tag></Select.Option>
                                    </Select>
                                ) : (
                                    renderHealthBadge(activeProject.health)
                                )}
                            </div>
                            <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center justify-between col-span-2">
                                <span className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Overall Progress</span>
                                <div className="flex items-center gap-3 w-44">
                                    <Progress percent={overallProgressPct} size="small" status="active" strokeColor={{ '0%': '#03488a', '100%': '#00aaf1' }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Unsaved Changes Sticky Prompt Banner ── */}
                    {isDirty && (
                        <div className="bg-amber-50 dark:bg-amber-950/80 border-b border-amber-200 dark:border-amber-800 px-6 py-2.5 flex items-center justify-between sticky top-0 z-20 shadow-sm">
                            <div className="flex items-center gap-2 text-xs font-bold text-amber-800 dark:text-amber-200">
                                <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />
                                <span>You have unsaved changes to this project.</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleDiscardChanges}
                                    disabled={savingProject}
                                    className="px-3 py-1 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                >
                                    Discard
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveProjectChanges}
                                    disabled={savingProject}
                                    className="px-4 py-1 text-xs font-bold bg-[#003263] hover:bg-[#022447] text-white rounded-lg transition-all flex items-center gap-1.5 shadow-sm active:scale-95"
                                >
                                    {savingProject ? <LoadingOutlined /> : <SaveOutlined />}
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── Navigation Tabs ── */}
                    <div className="px-6 bg-slate-50/50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                        <div className="flex gap-6">
                            {[
                                { key: 'overview', label: 'Overview', icon: <FileTextOutlined /> },
                                { key: 'tasks', label: `Milestones & Tasks (${regularTasks.length})`, icon: <CheckSquareOutlined /> },
                                { key: 'documents', label: `Documents (${documents.length})`, icon: <FilePdfOutlined /> },
                                { key: 'assets', label: `Assets (${assets.length})`, icon: <PictureOutlined /> },
                                { key: 'history', label: `Activity Log (${(activeProject.activityLogs || []).length})`, icon: <UnorderedListOutlined /> },
                            ].map((t) => (
                                <button
                                    key={t.key}
                                    type="button"
                                    onClick={() => setActiveTab(t.key)}
                                    className={`py-3.5 text-sm font-bold border-b-2 transition-all flex items-center gap-2 ${activeTab === t.key
                                        ? 'border-[#03488a] text-[#03488a] dark:text-[#a7c8ff]'
                                        : 'border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-white'
                                        }`}
                                >
                                    {t.icon}
                                    {t.label}
                                </button>
                            ))}
                        </div>

                        {activeTab === 'assets' && (
                            <div className="flex items-center gap-1 bg-slate-200 dark:bg-slate-800 p-1 rounded-lg">
                                <button
                                    type="button"
                                    onClick={() => setAssetViewMode('grid')}
                                    className={`p-1.5 rounded-md text-xs transition-all ${assetViewMode === 'grid' ? 'bg-white dark:bg-slate-700 shadow-sm text-blue-600' : 'text-slate-500'}`}
                                >
                                    <AppstoreOutlined />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setAssetViewMode('list')}
                                    className={`p-1.5 rounded-md text-xs transition-all ${assetViewMode === 'list' ? 'bg-white dark:bg-slate-700 shadow-sm text-blue-600' : 'text-slate-500'}`}
                                >
                                    <UnorderedListOutlined />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* ── Scrollable Tab Content ── */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar text-sm">

                        {/* ════════════════ TAB 1: OVERVIEW ════════════════ */}
                        {activeTab === 'overview' && (
                            <div className="space-y-6">
                                {/* Project Information */}
                                <div className="bg-slate-50 dark:bg-slate-900/40 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4">
                                    <h3 className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Project Information &amp; Classification</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Brand</p>
                                            <div className="mt-0.5">
                                                <Tag color="cyan" className="font-bold text-xs mr-0">
                                                    {activeProject.brandName || (activeProject.brandId?.includes('e360') ? 'e360Digital' : activeProject.brandId?.includes('cport') || activeProject.brandId?.includes('cellular') ? 'CellularPort' : 'DymaxTech')}
                                                </Tag>
                                         </div>
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Category</p>
                                            {isEditingProject && canEditThisProject(activeProject) ? (
                                                <Select
                                                    size="small"
                                                    value={editForm.category !== undefined ? editForm.category : (activeProject.category || activeProject.projectType || '')}
                                                    onChange={(val) => {
                                                        handleFieldChange('category', val);
                                                        handleFieldChange('subCategory', '');
                                                    }}
                                                    placeholder="Select Category..."
                                                    className="w-full text-xs font-semibold mt-1"
                                                >
                                                    {Object.keys(CATEGORIES_WITH_SUBCATEGORIES).map((cat) => (
                                                        <Select.Option key={cat} value={cat}>{cat}</Select.Option>
                                                    ))}
                                                    <Select.Option value="General">General</Select.Option>
                                                </Select>
                                            ) : (
                                                <p className="text-sm font-semibold text-[#003263] dark:text-[#a7c8ff]">{activeProject.category || activeProject.projectType || 'General'}</p>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Sub-Category</p>
                                            {isEditingProject && canEditThisProject(activeProject) ? (
                                                CATEGORIES_WITH_SUBCATEGORIES[editForm.category || activeProject.category] ? (
                                                    <Select
                                                        size="small"
                                                        value={editForm.subCategory !== undefined ? editForm.subCategory : (activeProject.subCategory || activeProject.projectSubType || '')}
                                                        onChange={(val) => handleFieldChange('subCategory', val)}
                                                        placeholder="Select Subcategory..."
                                                        className="w-full text-xs font-semibold mt-1"
                                                    >
                                                        {(CATEGORIES_WITH_SUBCATEGORIES[editForm.category || activeProject.category] || []).map((sub) => (
                                                            <Select.Option key={sub} value={sub}>{sub}</Select.Option>
                                                        ))}
                                                    </Select>
                                                ) : (
                                                    <Input
                                                        size="small"
                                                        value={editForm.subCategory !== undefined ? editForm.subCategory : (activeProject.subCategory || activeProject.projectSubType || '')}
                                                        onChange={(e) => handleFieldChange('subCategory', e.target.value)}
                                                        placeholder="Sub-Category..."
                                                        className="text-xs font-semibold mt-1 rounded-lg"
                                                    />
                                                )
                                            ) : (
                                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{activeProject.subCategory || activeProject.projectSubType || '—'}</p>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Duration Mode</p>
                                            {isEditingProject && canEditThisProject(activeProject) ? (
                                                <Select
                                                    size="small"
                                                    value={editForm.durationMode || activeProject.durationMode || 'Fixed'}
                                                    onChange={(val) => handleFieldChange('durationMode', val)}
                                                    className="w-full text-xs font-semibold mt-1"
                                                >
                                                    <Select.Option value="Fixed">Fixed Duration</Select.Option>
                                                    <Select.Option value="Ongoing">● Ongoing</Select.Option>
                                                </Select>
                                            ) : (
                                                <Tag color={activeProject.durationMode === 'Ongoing' ? 'green' : 'blue'} className="font-bold text-xs mt-0.5">
                                                    {activeProject.durationMode === 'Ongoing' ? '● Ongoing' : 'Fixed Duration'}
                                                </Tag>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Department</p>
                                            {isEditingProject && canEditThisProject(activeProject) ? (
                                                <Select
                                                    size="small"
                                                    value={editForm.department !== undefined ? editForm.department : (activeProject.department || 'Development')}
                                                    onChange={(val) => handleFieldChange('department', val)}
                                                    placeholder="Select Department..."
                                                    className="w-full text-xs font-semibold mt-1"
                                                >
                                                    {DEPARTMENTS.map((dept) => (
                                                        <Select.Option key={dept} value={dept}>{dept}</Select.Option>
                                                    ))}
                                                </Select>
                                            ) : (
                                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{activeProject.department || 'Development'}</p>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Project Owner</p>
                                            {isEditingProject && canEditThisProject(activeProject) ? (
                                                <Select
                                                    size="small"
                                                    showSearch
                                                    optionFilterProp="children"
                                                    value={editForm.createdBy !== undefined ? editForm.createdBy : (activeProject.createdBy?._id || activeProject.createdBy || activeProject.owner?._id || activeProject.owner || undefined)}
                                                    onChange={(val) => handleFieldChange('createdBy', val)}
                                                    placeholder="Assign Project Owner..."
                                                    className="w-full text-xs font-semibold mt-1"
                                                >
                                                    <Select.Option value="">None</Select.Option>
                                                    {employees.map((emp) => (
                                                        <Select.Option key={emp._id || emp.id} value={emp._id || emp.id}>
                                                            {formatName(emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim())}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            ) : (
                                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 capitalize">{getOwnerName()}</p>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Project Manager</p>
                                            {isEditingProject && canEditThisProject(activeProject) ? (
                                                <Select
                                                    size="small"
                                                    showSearch
                                                    optionFilterProp="children"
                                                    value={editForm.projectManager || activeProject.projectManager?._id || activeProject.projectManager || undefined}
                                                    onChange={(val) => handleFieldChange('projectManager', val)}
                                                    placeholder="Assign PM..."
                                                    className="w-full text-xs font-semibold mt-1"
                                                >
                                                    <Select.Option value="">None</Select.Option>
                                                    {employees.map((emp) => (
                                                        <Select.Option key={emp._id || emp.id} value={emp._id || emp.id}>
                                                            {formatName(emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim())}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            ) : (
                                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 capitalize">{getManagerName()}</p>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Start Date</p>
                                            {isEditingProject && canEditThisProject(activeProject) ? (
                                                <DatePicker
                                                    size="small"
                                                    value={editForm.startDate ? (dayjs.isDayjs(editForm.startDate) ? editForm.startDate : dayjs(editForm.startDate)) : null}
                                                    onChange={(date) => handleFieldChange('startDate', date)}
                                                    format="MMM DD, YYYY"
                                                    className="w-full text-xs font-semibold mt-1 rounded-lg"
                                                />
                                            ) : (
                                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{activeProject.startDate ? dayjs(activeProject.startDate).format('MMM DD, YYYY') : '—'}</p>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">End Date / Timeline</p>
                                            {isEditingProject && canEditThisProject(activeProject) ? (
                                                (editForm.durationMode || activeProject.durationMode) === 'Ongoing' ? (
                                                    <span className="text-xs font-medium text-slate-400 mt-2 block">Ongoing (No End Date)</span>
                                                ) : (
                                                    <DatePicker
                                                        size="small"
                                                        value={editForm.dueDate ? (dayjs.isDayjs(editForm.dueDate) ? editForm.dueDate : dayjs(editForm.dueDate)) : null}
                                                        onChange={(date) => handleFieldChange('dueDate', date)}
                                                        format="MMM DD, YYYY"
                                                        className="w-full text-xs font-semibold mt-1 rounded-lg"
                                                    />
                                                )
                                            ) : (
                                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                                                    {activeProject.durationMode === 'Ongoing' || (!activeProject.dueDate && activeProject.durationMode !== 'Fixed')
                                                        ? 'Ongoing (No End Date)'
                                                        : (activeProject.dueDate ? dayjs(activeProject.dueDate).format('MMM DD, YYYY') : '—')
                                                    }
                                                </p>
                                            )}
                                        </div>
                                        <div className="col-span-2 md:col-span-2">
                                            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Collaborators / Stakeholders</p>
                                            {isEditingProject && canEditThisProject(activeProject) ? (
                                                <Select
                                                    mode="multiple"
                                                    size="small"
                                                    showSearch
                                                    allowClear
                                                    optionFilterProp="children"
                                                    value={Array.isArray(editForm.members) ? editForm.members : extractMemberIds(activeProject)}
                                                    onChange={(vals) => handleFieldChange('members', vals)}
                                                    placeholder="Select project collaborators & stakeholders..."
                                                    className="w-full text-xs font-semibold mt-1"
                                                    maxTagCount="responsive"
                                                >
                                                    {employees.map((emp) => (
                                                        <Select.Option key={emp._id || emp.id} value={String(emp._id || emp.id)}>
                                                            {formatName(emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim())}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            ) : (
                                                collaborators.length > 0 ? (
                                                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                                        {collaborators.map((member, i) => (
                                                            <Tag key={member._id || i} color="blue" className="mr-0 text-xs font-semibold rounded-md px-2 py-0.5">
                                                                {member.name}
                                                            </Tag>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-sm font-semibold text-slate-400">Unassigned</p>
                                                )
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Overall Health & Completion Breakdown (Section 13) */}
                                <div className="bg-slate-50 dark:bg-slate-900/40 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Health &amp; Progress Metrics</h3>
                                        {renderHealthBadge(activeProject.health)}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Task Completion</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <Progress percent={taskProgressPct} size="small" strokeColor="#2563eb" />
                                         </div>
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Milestone Completion</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <Progress percent={milestoneProgressPct} size="small" strokeColor="#10b981" />
                                         </div>
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Overall Completion</p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <Progress percent={overallProgressPct} size="small" strokeColor="#03488a" status="active" />
                                         </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Proposal Reference Card */}
                                {(proposalTitle || proposalUrl) && (
                                    <div className="bg-indigo-50/70 dark:bg-slate-900/60 p-5 rounded-2xl border border-indigo-200/80 dark:border-slate-800 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <FileTextOutlined className="text-indigo-600 text-lg" />
                                                <h3 className="text-xs font-bold text-slate-800 dark:text-slate-100 uppercase tracking-wider">Associated Client Proposal</h3>
                                         </div>
                                            <Tag color="geekblue" className="font-bold text-xs mr-0">Proposal</Tag>
                                        </div>
                                        <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 flex flex-col md:flex-row md:items-center justify-between gap-4">
                                            <div className="space-y-1">
                                                <p className="text-sm font-bold text-[#003263] dark:text-[#a7c8ff]">
                                                    {proposalTitle || 'Client Proposal'}
                                                </p>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                                    Linked from CRE project intake
                                                </p>
                                                {proposalUrl && (
                                                    <p className="text-xs text-slate-500 dark:text-slate-400 break-all">
                                                        Proposal Link: <a href={proposalUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 font-bold hover:underline">{proposalUrl}</a>
                                                    </p>
                                                )}
                                         </div>
                                            {proposalUrl && (
                                                <a
                                                    href={proposalUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 transition-all shadow-sm flex items-center gap-1.5 shrink-0"
                                                >
                                                    <span>View Proposal ↗</span>
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Questionnaire Reference Card (Section 9 & 12) */}
                                {(activeProject.questionnaireTemplateName || activeProject.linkedQuestionnaire || (activeProject.questionnaires && activeProject.questionnaires.length > 0)) && (
                                    <div className="bg-blue-50/70 dark:bg-slate-900/60 p-5 rounded-2xl border border-blue-200/80 dark:border-slate-800 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <FileTextOutlined className="text-blue-600 text-lg" />
                                                <h3 className="text-xs font-bold text-slate-800 dark:text-slate-100 uppercase tracking-wider">Associated Client Questionnaire</h3>
                                         </div>
                                            <div className="flex items-center gap-2">
                                                <Tag color="emerald" className="font-bold text-xs">Status: Submitted</Tag>
                                                <Tag color="blue" className="mr-0 font-bold text-xs">Client Intake Form</Tag>
                                         </div>
                                        </div>
                                        <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 flex flex-col md:flex-row md:items-center justify-between gap-4">
                                            {(() => {
                                                const allQs = Array.from(new Set([
                                                    ...(Array.isArray(activeProject.questionnaires) ? activeProject.questionnaires : []),
                                                    ...(activeProject.questionnaireTemplateName ? [activeProject.questionnaireTemplateName] : []),
                                                    ...(activeProject.linkedQuestionnaire?.name ? [activeProject.linkedQuestionnaire.name] : []),
                                                ])).filter(Boolean);
                                                const primaryQ = allQs[0] || 'Client Intake Questionnaire';
                                                const extraQs = allQs.slice(1);
                                                return (
                                                    <div className="space-y-1">
                                                        <p className="text-sm font-bold text-[#003263] dark:text-[#a7c8ff]">
                                                            {primaryQ}
                                                        </p>
                                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                                            Submitted: {activeProject.linkedQuestionnaire?.submittedAt ? dayjs(activeProject.linkedQuestionnaire.submittedAt).format('MMM DD, YYYY') : dayjs(activeProject.createdAt).format('MMM DD, YYYY')}
                                                            {activeProject.clientName && ` • Submitted By: ${activeProject.clientName}`}
                                                        </p>
                                                        {extraQs.length > 0 && (
                                                            <div className="flex flex-wrap gap-1.5 pt-1.5">
                                                                <span className="text-[11px] font-bold text-slate-400">Additional Forms:</span>
                                                                {extraQs.map((q, i) => (
                                                                    <span key={i} className="text-[11px] bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-md text-slate-700 dark:text-slate-200 font-bold">
                                                                        📋 {q}
                                                                    </span>
                                                                ))}
                                         </div>
                                                        )}
                                         </div>
                                                );
                                            })()}
                                            <button
                                                type="button"
                                                onClick={() => setQuestionnaireAnswersOpen(true)}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold hover:bg-blue-700 transition-all shadow-sm flex items-center gap-1.5 shrink-0"
                                            >
                                                <span>View Questionnaire ↗</span>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Operational Scope & Description */}
                                <div className="bg-slate-50 dark:bg-slate-900/40 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-2">
                                    <h3 className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Description &amp; Operational Scope</h3>
                                    {isEditingProject && canEditThisProject(activeProject) ? (
                                        <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#1e293b]">
                                            <style>{`
                                                .pdm-quill .ql-container.ql-snow { border: none !important; min-height: 100px; font-size: 13px; }
                                                .pdm-quill .ql-toolbar.ql-snow { border: none !important; border-bottom: 1px solid #e2e8f0 !important; background: #f8fafc; }
                                                .dark .pdm-quill .ql-toolbar.ql-snow { background: #111827; border-bottom: 1px solid #374151 !important; }
                                            `}</style>
                                            <ReactQuill
                                                className="pdm-quill"
                                                theme="snow"
                                                value={decodeHtmlEntities(editForm.description !== undefined ? editForm.description : (activeProject.description || ''))}
                                                onChange={(val) => handleFieldChange('description', val)}
                                                placeholder="Enter project description & operational scope..."
                                            />
                                        </div>
                                    ) : (
                                        activeProject.description && stripHtmlTags(activeProject.description) ? (
                                            <div
                                                className="prose dark:prose-invert text-sm max-w-none text-slate-700 dark:text-slate-300 [&_p]:mb-2 [&_p:last-child]:mb-0"
                                                dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(activeProject.description) }}
                                            />
                                        ) : (
                                            <p className="text-xs text-slate-400 italic">No description provided.</p>
                                        )
                                    )}
                                </div>

                                {/* Goal & Objectives */}
                                {(isEditingProject || canEditThisProject(activeProject) || (Array.isArray(activeProject.smartGoals) && activeProject.smartGoals.length > 0) || activeProject.goal || (activeProject.objectives && activeProject.objectives.length > 0)) && (() => {
                                    const currentEditableGoals = editForm.smartGoals !== undefined
                                        ? editForm.smartGoals
                                        : extractProjectSmartGoals(activeProject);
                                    return (
                                        <div className="space-y-4 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-slate-900 dark:to-blue-950/20 p-5 rounded-2xl border border-blue-200/60 dark:border-blue-900/40">
                                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <AimOutlined className="text-blue-600 text-lg" />
                                                        <h3 className="text-sm font-bold text-slate-800 dark:text-white flex items-center gap-1.5">
                                                            Smart Goals &amp; Objectives (Project-Level)
                                                            {isEditingProject && <span className="text-xs font-normal text-slate-400 dark:text-slate-500">(Optional)</span>}
                                                        </h3>
                                                    </div>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                                        {isEditingProject
                                                            ? 'Edit existing goals below, add new goals manually, or let AI generate targeted smart goals'
                                                            : 'Targeted project goals, measurable objectives, and success criteria'}
                                                    </p>
                                                </div>

                                                {canEditThisProject(activeProject) && (
                                                    isEditingProject ? (
                                                        <div className="flex items-center gap-2 shrink-0 flex-wrap">
                                                            <button
                                                                type="button"
                                                                onClick={handleGenerateSmartGoals}
                                                                disabled={aiGeneratingGoals}
                                                                className="px-3.5 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:opacity-90 transition-all flex items-center gap-1.5 shadow-sm disabled:opacity-50"
                                                            >
                                                                {aiGeneratingGoals ? <LoadingOutlined /> : <RobotOutlined />}
                                                                {aiGeneratingGoals ? 'Analyzing & Generating...' : 'Create Smart Goals with AI'}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={openAddGoal}
                                                                className="px-3.5 py-2 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 transition-all flex items-center gap-1 shadow-xs"
                                                            >
                                                                <PlusOutlined /> Add Goal Manually
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                handleDiscardChanges();
                                                                setIsEditingProject(true);
                                                            }}
                                                            className="px-3 py-1.5 rounded-xl text-xs font-bold text-blue-600 dark:text-blue-400 bg-white dark:bg-slate-800 border border-blue-200 dark:border-slate-700 hover:bg-blue-50 transition-all flex items-center gap-1.5 shrink-0 shadow-xs"
                                                        >
                                                            <EditOutlined /> Manage Goals
                                                        </button>
                                                    )
                                                )}
                                            </div>
                                            {/* Goals List */}
                                            {isEditingProject ? (
                                                currentEditableGoals.length > 0 ? (
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 pt-2">
                                                        {currentEditableGoals.map((g) => (
                                                            <div key={g.id || g._id} className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 space-y-2.5 relative shadow-sm flex flex-col justify-between">
                                                                <div className="space-y-1.5">
                                                                    <div className="flex justify-between items-start gap-2">
                                                                        <span
                                                                            onClick={() => openEditGoal(g)}
                                                                            className="text-sm font-bold text-[#003263] dark:text-[#a7c8ff] line-clamp-2 leading-snug cursor-pointer hover:text-blue-600 transition-colors"
                                                                            title="Click to edit this goal"
                                                                        >
                                                                            {g.name}
                                                                        </span>
                                                                        <div className="flex items-center gap-1 shrink-0">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => openEditGoal(g)}
                                                                                className="px-2 py-0.5 text-[11px] font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/60 dark:text-blue-300 rounded-md transition-colors flex items-center gap-1"
                                                                                title="Edit Goal"
                                                                            >
                                                                                <EditOutlined style={{ fontSize: 11 }} /> Edit
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleRemoveGoal(g.id || g._id)}
                                                                                className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-md transition-colors"
                                                                                title="Remove Goal"
                                                                            >
                                                                                <DeleteOutlined style={{ fontSize: 12 }} />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    {g.description && <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-3 leading-relaxed">{stripHtmlTags(g.description)}</p>}
                                                                    {g.successCriteria && (
                                                                        <p className="text-[11px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/60 p-2 rounded-lg border border-slate-100 dark:border-slate-800">
                                                                            <span className="font-bold text-slate-700 dark:text-slate-300">Criteria:</span> {stripHtmlTags(g.successCriteria)}
                                                                        </p>
                                                                    )}
                                                                </div>

                                                                <div className="pt-2 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 font-medium">
                                                                    <span>Target: {g.targetDate ? dayjs(g.targetDate).format('MMM DD, YYYY') : 'Project Due'}</span>
                                                                    <Tag color={g.priority === 'Critical' ? 'error' : g.priority === 'High' ? 'warning' : 'blue'} className="mr-0 text-xs font-semibold px-2 py-0.5 rounded-md">
                                                                        {g.priority || 'Medium'}
                                                                    </Tag>
                                                                </div>
                                                            </div>
                                                        ))}
                                                        {/* Quick Add New Goal Card */}
                                                        <button
                                                            type="button"
                                                            onClick={openAddGoal}
                                                            className="min-h-[140px] rounded-xl border-2 border-dashed border-blue-300 dark:border-blue-800/70 bg-white/60 dark:bg-slate-800/40 hover:bg-blue-50/70 dark:hover:bg-slate-800 transition-all flex flex-col items-center justify-center gap-2 p-4 text-blue-600 dark:text-blue-400 group"
                                                        >
                                                            <span className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-950 flex items-center justify-center group-hover:scale-110 transition-transform">
                                                                <PlusOutlined className="text-base" />
                                                            </span>
                                                            <span className="text-xs font-bold">+ Add New Goal</span>
                                                            <span className="text-[11px] text-slate-400 font-normal">Add another S.M.A.R.T. goal</span>
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-6 border-2 border-dashed border-blue-200 dark:border-blue-900/40 rounded-xl bg-white/50 dark:bg-slate-900/20 space-y-2">
                                                        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">No Smart Goals added yet. Click above to let AI analyze your project or add one manually!</p>
                                                        <button
                                                            type="button"
                                                            onClick={openAddGoal}
                                                            className="px-3.5 py-1.5 rounded-xl text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 transition-all inline-flex items-center gap-1"
                                                        >
                                                            <PlusOutlined /> Add First Goal
                                                        </button>
                                                    </div>
                                                )
                                            ) : (Array.isArray(activeProject.smartGoals) && activeProject.smartGoals.length > 0) ? (
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 pt-2">
                                                    {activeProject.smartGoals.map((g, idx) => (
                                                        <div key={g.id || g._id || idx} className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 space-y-2.5 shadow-sm flex flex-col justify-between">
                                                            <div className="space-y-1.5">
                                                                <span className="text-sm font-bold text-[#003263] dark:text-[#a7c8ff] line-clamp-2 leading-snug">{g.name}</span>
                                                                {g.description && <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-3 leading-relaxed">{stripHtmlTags(g.description)}</p>}
                                                                {g.successCriteria && (
                                                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/60 p-2 rounded-lg border border-slate-100 dark:border-slate-800">
                                                                        <span className="font-bold text-slate-700 dark:text-slate-300">Criteria:</span> {stripHtmlTags(g.successCriteria)}
                                                                    </p>
                                                                )}
                                                            </div>

                                                            <div className="pt-2 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 font-medium">
                                                                <span>Target: {g.targetDate ? dayjs(g.targetDate).format('MMM DD, YYYY') : 'Project Due'}</span>
                                                                <Tag color={g.priority === 'Critical' ? 'error' : g.priority === 'High' ? 'warning' : 'blue'} className="mr-0 text-xs font-semibold px-2 py-0.5 rounded-md">
                                                                    {g.priority || 'Medium'}
                                                                </Tag>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                (activeProject.goal || (activeProject.objectives && activeProject.objectives.length > 0)) ? (
                                                    <div className="space-y-3 pt-1">
                                                        {activeProject.goal && (
                                                            <div className="bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700">
                                                                <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Primary Goal</p>
                                                                <p className="font-bold text-sm text-[#003263] dark:text-[#a7c8ff] mt-0.5">{stripHtmlTags(activeProject.goal)}</p>
                                                            </div>
                                                        )}
                                                        {activeProject.objectives && activeProject.objectives.length > 0 && (
                                                            <div className="space-y-1.5 pt-1">
                                                                <p className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Key Objectives</p>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                                    {activeProject.objectives.map((obj, i) => (
                                                                        <div key={i} className="bg-white dark:bg-slate-800 p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-semibold flex items-center gap-2">
                                                                            <span className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 flex items-center justify-center font-bold text-[10px] shrink-0">{i + 1}</span>
                                                                            <span className="truncate text-slate-700 dark:text-slate-200">{stripHtmlTags(obj)}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <p className="text-xs text-slate-400 italic">No project goals defined yet. Click &ldquo;Manage Goals&rdquo; or &ldquo;Edit Project&rdquo; to add Smart Goals.</p>
                                                )
                                            )}
                                        </div>
                                    );
                                })()}

                                {/* Workflow & View Configuration */}
                                <div className="bg-slate-50 dark:bg-slate-900/40 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
                                    <h3 className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Workflow &amp; View Configuration</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                                        <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200 dark:border-slate-700">
                                            <p className="text-slate-400 font-medium">Sprint Management</p>
                                            <p className="font-bold text-slate-800 dark:text-slate-200 mt-1 flex items-center gap-1.5">
                                                <span className={`w-2 h-2 rounded-full ${(activeProject.sprintManagement ?? activeProject.autoSprintPlanning ?? true) ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                                                {(activeProject.sprintManagement ?? activeProject.autoSprintPlanning ?? true) ? 'Enabled (ON)' : 'Disabled (OFF)'}
                                            </p>
                                        </div>
                                        <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200 dark:border-slate-700">
                                            <p className="text-slate-400 font-medium">Default Opening View</p>
                                            <p className="font-bold text-blue-600 dark:text-blue-400 mt-1">
                                                {activeProject.defaultView || activeProject.customFields?.defaultView || 'Kanban Board View'}
                                            </p>
                                        </div>
                                        <div className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200 dark:border-slate-700">
                                            <p className="text-slate-400 font-medium">Enabled Views</p>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {(() => {
                                                    const views = activeProject.availableViews || activeProject.customFields?.availableViews || { list: true, kanban: true, timeline: true, sprint: true };
                                                    const labels = [];
                                                    if (views.list !== false) labels.push('List');
                                                    if (views.kanban !== false) labels.push('Kanban');
                                                    if (views.timeline !== false) labels.push('Timeline');
                                                    if ((activeProject.sprintManagement ?? activeProject.autoSprintPlanning ?? true) && views.sprint !== false) labels.push('Sprint');
                                                    return labels.map((l) => (
                                                        <span key={l} className="bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold px-1.5 py-0.5 rounded text-[10px]">
                                                            {l}
                                                        </span>
                                                    ));
                                                })()}
                                         </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Requirements Checklist */}
                                {Array.isArray(activeProject.requirementChecklist) && activeProject.requirementChecklist.length > 0 && (
                                    <div className="bg-slate-50 dark:bg-slate-900/40 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <h3 className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                                                Project Requirements Checklist ({activeProject.requirementChecklist.filter(x => x?.met).length}/{activeProject.requirementChecklist.length} Met)
                                            </h3>
                                            {activeProject.requirementChecklist.some(x => x?.status === 'Resubmitted' || x?.decision === 'Resubmitted') && (
                                                <span className="text-[11px] font-bold text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-950/50 border border-sky-200 dark:border-sky-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                                                    🔄 Resubmitted Items
                                                </span>
                                            )}
                                        </div>
                                        <div className="space-y-2.5">
                                            {activeProject.requirementChecklist.map((req, idx) => {
                                                const isResubmitted = req.status === 'Resubmitted' || req.decision === 'Resubmitted';
                                                const isApproved = req.status === 'Approved' || (req.met && req.decision === 'Approved');
                                                const isRejected = req.rejected || req.status === 'Rejected' || req.decision === 'Rejected' || Boolean(req.rejectionNote && !req.met && !isResubmitted);

                                                let cardBorder = 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800';
                                                if (isResubmitted) {
                                                    cardBorder = 'border-sky-300 dark:border-sky-800 bg-sky-50/40 dark:bg-sky-950/20';
                                                } else if (isApproved || req.met) {
                                                    cardBorder = 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/20 dark:bg-emerald-950/10';
                                                } else if (isRejected) {
                                                    cardBorder = 'border-rose-200 dark:border-rose-900/50 bg-rose-50/20 dark:bg-rose-950/10';
                                                }

                                                return (
                                                    <div key={idx} className={`p-3.5 rounded-xl border ${cardBorder} text-xs transition-all space-y-2`}>
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div className="flex items-start gap-2.5 flex-1 min-w-0">
                                                                <span className={`w-5 h-5 mt-0.5 rounded-full flex items-center justify-center font-bold text-[11px] shrink-0 ${isResubmitted
                                                                    ? 'bg-sky-100 text-sky-700 dark:bg-sky-900 dark:text-sky-300'
                                                                    : (req.met ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/60 dark:text-emerald-400' : 'bg-amber-100 text-amber-600')
                                                                    }`}>
                                                                    {isResubmitted ? '🔄' : (req.met ? '✓' : '!')}
                                                                </span>
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <span className="font-semibold text-slate-800 dark:text-slate-100 text-sm">
                                                                            {req.label}
                                                                        </span>
                                                                        {req.category && (
                                                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                                                                {req.category}
                                                                            </span>
                                                                        )}
                                                                        {req.required && (
                                                                            <Tag color="red" className="text-[10px] font-bold py-0 m-0">Required</Tag>
                                                                        )}
                                                                        {isResubmitted && (
                                                                            <Tag color="cyan" className="text-[10px] font-bold py-0 m-0">🔄 Resubmitted by CRE</Tag>
                                                                        )}
                                                                        {isApproved && (
                                                                            <Tag color="green" className="text-[10px] font-bold py-0 m-0">✓ Approved</Tag>
                                                                        )}
                                                                        {isRejected && (
                                                                            <Tag color="volcano" className="text-[10px] font-bold py-0 m-0">✕ Rejected</Tag>
                                                                        )}
                                         </div>
                                                                    {req.detail && (
                                                                        <p className="text-slate-500 dark:text-slate-400 text-[11px] mt-0.5">{req.detail}</p>
                                                                    )}
                                         </div>
                                         </div>
                                                            {req.note && <span className="text-slate-400 font-medium italic text-right shrink-0">{req.note}</span>}
                                         </div>

                                                        {/* Resubmission details box */}
                                                        {isResubmitted && (
                                                            <div className="bg-sky-50 dark:bg-sky-900/30 border border-sky-200 dark:border-sky-800/60 rounded-lg p-2.5 space-y-1">
                                                                {req.fixNote && (
                                                                    <p className="text-sky-900 dark:text-sky-200 text-[11px]">
                                                                        <span className="font-bold">🛠️ Fix Note from CRE:</span> {req.fixNote}
                                                                    </p>
                                                                )}
                                                                <div className="flex items-center gap-3 text-[10px] text-sky-700 dark:text-sky-400 flex-wrap">
                                                                    {req.acknowledgedBy && (
                                                                        <span>👤 Resubmitted by: <strong className="font-semibold">{req.acknowledgedBy}</strong> {req.acknowledgedByEmail ? `(${req.acknowledgedByEmail})` : ''}</span>
                                                                    )}
                                                                    {req.acknowledgedAt && (
                                                                        <span>🕒 {dayjs(req.acknowledgedAt).format('MMM D, YYYY h:mm A')}</span>
                                                                    )}
                                         </div>
                                                                {req.previousRejectionNote && (
                                                                    <p className="text-slate-500 dark:text-slate-400 text-[10px] italic border-t border-sky-200/60 dark:border-sky-800/40 pt-1 mt-1">
                                                                        Previous Rejection Reason: "{req.previousRejectionNote}"
                                                                    </p>
                                                                )}
                                         </div>
                                                        )}

                                                        {/* Approval details box */}
                                                        {(req.approvedBy || req.approvalReason || req.reason) && isApproved && (
                                                            <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/60 rounded-lg p-2 space-y-0.5 text-[11px] text-emerald-900 dark:text-emerald-200">
                                                                {(req.approvalReason || req.reason) && (
                                                                    <p><span className="font-bold">✓ Approval Reason:</span> {req.approvalReason || req.reason}</p>
                                                                )}
                                                                <div className="flex items-center gap-3 text-[10px] text-emerald-700 dark:text-emerald-400">
                                                                    {req.approvedBy && <span>👤 Approved by: <strong className="font-semibold">{req.approvedBy}</strong></span>}
                                                                    {req.approvedAt && <span>🕒 {dayjs(req.approvedAt).format('MMM D, YYYY h:mm A')}</span>}
                                         </div>
                                         </div>
                                                        )}

                                                        {/* Rejection details box */}
                                                        {(req.rejectionNote || (req.reason && isRejected)) && !isResubmitted && (
                                                            <div className="bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/60 rounded-lg p-2 space-y-0.5 text-[11px] text-rose-900 dark:text-rose-200">
                                                                <p><span className="font-bold">✕ Rejection Reason:</span> {req.rejectionNote || req.reason}</p>
                                                                <div className="flex items-center gap-3 text-[10px] text-rose-700 dark:text-rose-400">
                                                                    {req.rejectedBy && <span>👤 Rejected by: <strong className="font-semibold">{req.rejectedBy}</strong></span>}
                                                                    {req.rejectedAt && <span>🕒 {dayjs(req.rejectedAt).format('MMM D, YYYY h:mm A')}</span>}
                                         </div>
                                         </div>
                                                        )}
                                         </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ════════════════ TAB: ALL PROJECT TASKS ════════════════ */}
                        {activeTab === 'tasks' && (
                            <div className="space-y-4">
                                {/* KPI Metrics Summary Bar */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <div className="bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-800">
                                        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Total Tasks</p>
                                        <div className="flex items-baseline gap-1.5 mt-0.5">
                                            <p className="text-xl font-bold text-[#003263] dark:text-[#a7c8ff]">{regularTasks.length}</p>
                                            <span className="text-[11px] font-semibold text-slate-400">({allParentTasks.length} parent · {allSubtasks.length} subtasks)</span>
                                        </div>
                                    </div>
                                    <div className="bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-800">
                                        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Top-Level Parents</p>
                                        <p className="text-xl font-bold text-indigo-600 dark:text-indigo-400 mt-0.5">{allParentTasks.length}</p>
                                    </div>
                                    <div className="bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-800">
                                        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Nested Subtasks</p>
                                        <p className="text-xl font-bold text-purple-600 dark:text-purple-400 mt-0.5">{allSubtasks.length}</p>
                                    </div>
                                    <div className="bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-800">
                                        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Completed</p>
                                        <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">{completedTasks}</p>
                                    </div>
                                </div>

                                {/* Search, View Modes & Filters Bar */}
                                <div className="bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-800 flex flex-col gap-3">
                                    {/* Top Row: Wide Search Bar + View Mode Switcher + Add Milestone */}
                                    <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
                                        <div className="relative flex-1 min-w-[260px]">
                                            <SearchOutlined className="absolute left-3.5 top-2.5 text-slate-400 text-xs" />
                                            <input
                                                type="text"
                                                placeholder="Search tasks by title, code (TSK-...), or assignee..."
                                                value={taskSearch}
                                                onChange={(e) => { setTaskSearch(e.target.value); setTaskPage(1); }}
                                                className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 pl-9 pr-3 py-2 rounded-xl text-xs font-semibold text-slate-800 dark:text-slate-200 placeholder-slate-400 outline-none focus:ring-2 focus:ring-blue-500 transition-all shadow-2xs"
                                            />
                                        </div>

                                        <div className="flex items-center gap-2.5 flex-wrap shrink-0">
                                            {/* View Mode Switcher: Hierarchy vs Tree vs Flat */}
                                            <div className="flex items-center bg-slate-200/80 dark:bg-slate-800 p-1 rounded-xl">
                                                <button
                                                    type="button"
                                                    onClick={() => { setTaskViewMode('hierarchy'); setTaskPage(1); }}
                                                    className={`px-2.5 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${taskViewMode === 'hierarchy'
                                                        ? 'bg-white dark:bg-slate-700 text-[#03488a] dark:text-[#a7c8ff] shadow-sm'
                                                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-200'
                                                        }`}
                                                    title="Milestone > Task > Subtasks Hierarchy"
                                                >
                                                    <ApartmentOutlined /> Hierarchy
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => { setTaskViewMode('tree'); setTaskPage(1); }}
                                                    className={`px-2.5 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${taskViewMode === 'tree'
                                                        ? 'bg-white dark:bg-slate-700 text-[#03488a] dark:text-[#a7c8ff] shadow-sm'
                                                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-200'
                                                        }`}
                                                    title="Group subtasks under parent tasks (Tree View)"
                                                >
                                                    <BranchesOutlined /> Task Tree
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => { setTaskViewMode('flat'); setTaskPage(1); }}
                                                    className={`px-2.5 py-1.5 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${taskViewMode === 'flat'
                                                        ? 'bg-white dark:bg-slate-700 text-[#03488a] dark:text-[#a7c8ff] shadow-sm'
                                                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-200'
                                                        }`}
                                                    title="View all tasks flat with subtask badges"
                                                >
                                                    <MenuOutlined /> Flat List
                                                </button>
                                            </div>

                                            {canManageMilestone(activeProject) && (
                                                <button
                                                    type="button"
                                                    onClick={openAddMilestone}
                                                    className="px-3.5 py-2 text-xs font-bold text-white bg-[#03488a] hover:bg-[#003263] rounded-xl transition-all flex items-center gap-1.5 shadow-sm shrink-0 active:scale-95"
                                                >
                                                    <PlusOutlined /> Add Milestone
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Bottom Row: Expand/Collapse Controls & Filter Selects */}
                                    <div className="flex items-center justify-between gap-2 flex-wrap pt-2 border-t border-slate-200/70 dark:border-slate-800">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            {taskViewMode === 'hierarchy' && (
                                                <div className="flex items-center gap-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={expandAllHierarchy}
                                                        className="px-2.5 py-1 text-[11px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50 hover:bg-blue-100 rounded-lg transition-colors"
                                                    >
                                                        Expand All
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={collapseAllHierarchy}
                                                        className="px-2.5 py-1 text-[11px] font-bold text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 rounded-lg transition-colors"
                                                    >
                                                        Collapse All
                                                    </button>
                                                </div>
                                            )}

                                            {taskViewMode === 'tree' && (
                                                <div className="flex items-center gap-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={expandAllTree}
                                                        className="px-2.5 py-1 text-[11px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50 hover:bg-blue-100 rounded-lg transition-colors"
                                                    >
                                                        Expand All
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={collapseAllTree}
                                                        className="px-2.5 py-1 text-[11px] font-bold text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 rounded-lg transition-colors"
                                                    >
                                                        Collapse All
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-2 flex-wrap">
                                            {taskViewMode === 'flat' && (
                                                <Select
                                                    size="small"
                                                    value={taskTypeFilter}
                                                    onChange={(val) => { setTaskTypeFilter(val); setTaskPage(1); }}
                                                    className="min-w-[140px] text-xs font-semibold"
                                                >
                                                    <Select.Option value="all">All Types ({regularTasks.length})</Select.Option>
                                                    <Select.Option value="parents">Parents Only ({allParentTasks.length})</Select.Option>
                                                    <Select.Option value="subtasks">Subtasks Only ({allSubtasks.length})</Select.Option>
                                                </Select>
                                            )}

                                            <Select
                                                size="small"
                                                value={taskSortBy}
                                                onChange={(val) => setTaskSortBy(val)}
                                                className="min-w-[150px] text-xs font-semibold"
                                            >
                                                <Select.Option value="default">Sort: Default</Select.Option>
                                                <Select.Option value="due_asc">Sort: Due Date (Earliest)</Select.Option>
                                                <Select.Option value="due_desc">Sort: Due Date (Latest)</Select.Option>
                                                <Select.Option value="assignee_asc">Sort: Assignee (A-Z)</Select.Option>
                                                <Select.Option value="assignee_desc">Sort: Assignee (Z-A)</Select.Option>
                                                <Select.Option value="priority_desc">Sort: Priority (High to Low)</Select.Option>
                                                <Select.Option value="priority_asc">Sort: Priority (Low to High)</Select.Option>
                                                <Select.Option value="name_asc">Sort: Name (A-Z)</Select.Option>
                                                <Select.Option value="name_desc">Sort: Name (Z-A)</Select.Option>
                                            </Select>

                                            <Select
                                                size="small"
                                                value={taskStatusFilter}
                                                onChange={(val) => { setTaskStatusFilter(val); setTaskPage(1); }}
                                                className="min-w-[130px] text-xs font-semibold"
                                            >
                                                <Select.Option value="all">All Statuses</Select.Option>
                                                <Select.Option value="Backlog">Backlog</Select.Option>
                                                <Select.Option value="In Progress">In Progress</Select.Option>
                                                <Select.Option value="Under Review">Under Review</Select.Option>
                                                <Select.Option value="Completed">Completed</Select.Option>
                                            </Select>

                                            <Select
                                                size="small"
                                                value={taskAssigneeFilter}
                                                onChange={(val) => { setTaskAssigneeFilter(val); setTaskPage(1); }}
                                                className="min-w-[145px] text-xs font-semibold"
                                            >
                                                <Select.Option value="all">All Assignments</Select.Option>
                                                <Select.Option value="assigned">Assigned ({assignedTasksCount})</Select.Option>
                                                <Select.Option value="unassigned">Unassigned ({unassignedTasksCount})</Select.Option>
                                            </Select>

                                            <Select
                                                size="small"
                                                value={taskPageSize}
                                                onChange={(val) => { setTaskPageSize(val); setTaskPage(1); }}
                                                className="w-28 text-xs font-semibold"
                                            >
                                                <Select.Option value={15}>15 / page</Select.Option>
                                                <Select.Option value={30}>30 / page</Select.Option>
                                                <Select.Option value={50}>50 / page</Select.Option>
                                                <Select.Option value={9999}>View All</Select.Option>
                                            </Select>
                                        </div>
                                    </div>
                                </div>

                                {/* ── MILESTONE > TASK > SUBTASKS HIERARCHY VIEW (Image 5 Spec) ── */}
                                {taskViewMode === 'hierarchy' && (
                                    <div className="space-y-4">
                                        {milestoneHierarchyGroups.length === 0 && unassignedParentTasks.length === 0 ? (
                                            <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
                                                <p className="text-sm font-bold text-slate-400">No project tasks found matching your filters.</p>
                                         </div>
                                        ) : (
                                            <>
                                                {milestoneHierarchyGroups.map((group) => {
                                                    const { milestone: m, mKey, mIndex, parentTasks, totalTasksCount, completedTasksCount } = group;
                                                    const isCollapsed = collapsedMilestoneIds.has(mKey);

                                                    return (
                                                        <div key={mKey} className="bg-white dark:bg-slate-850 rounded-2xl border border-slate-200 dark:border-slate-750 shadow-xs overflow-hidden">
                                                            {/* Milestone Header */}
                                                            <div
                                                                onClick={() => toggleMilestoneCollapse(mKey)}
                                                                className="px-4 py-3 bg-slate-100/90 dark:bg-slate-800/90 hover:bg-slate-200/70 dark:hover:bg-slate-750 flex items-center justify-between cursor-pointer select-none transition-colors border-b border-slate-200/80 dark:border-slate-700/80"
                                                            >
                                                                <div className="flex items-center gap-2.5 min-w-0">
                                                                    <span className="text-slate-500 text-xs transition-transform">
                                                                        {isCollapsed ? <RightOutlined /> : <DownOutlined />}
                                                                    </span>
                                                                    <span className="font-mono text-xs font-bold text-blue-600 bg-blue-100 dark:bg-blue-950 px-2 py-0.5 rounded-md">
                                                                        M{mIndex + 1}
                                                                    </span>
                                                                    <span className="text-sm font-bold text-[#003263] dark:text-[#a7c8ff] truncate">
                                                                        {m.name || m.title}
                                                                    </span>
                                                                    <span className="text-xs font-semibold text-slate-400 shrink-0">
                                                                        ({completedTasksCount}/{totalTasksCount} done)
                                                                    </span>
                                         </div>

                                                                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                                                                    {Array.isArray(m.deliverables) && m.deliverables.length > 0 && (() => {
                                                                        const dels = m.deliverables;
                                                                        const doneCount = dels.filter((d) => Boolean(d.done || d.completed)).length;
                                                                        const allDone = doneCount === dels.length;
                                                                        return (
                                                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${allDone ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'}`}>
                                                                                {doneCount}/{dels.length} Verified
                                                                            </span>
                                                                        );
                                                                    })()}
                                                                    {m.dueDate && (
                                                                        <span className="text-xs text-slate-500 dark:text-slate-400 font-medium hidden sm:inline">
                                                                            Due {dayjs(m.dueDate).format('MMM DD, YYYY')}
                                                                        </span>
                                                                    )}
                                                                    <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1.5">
                                                                        <Tooltip title={!canManageMilestone(activeProject) ? "Only the Project Manager or Admin can edit milestone status" : ""}>
                                                                            <div>
                                                                                <Select
                                                                                    value={m.status || 'Pending'}
                                                                                    onChange={(newVal) => handleMilestoneStatusChange(mIndex, newVal)}
                                                                                    disabled={!canManageMilestone(activeProject) || updatingMilestoneIndex === mIndex}
                                                                                    loading={updatingMilestoneIndex === mIndex}
                                                                                    size="small"
                                                                                    className="w-32 font-bold text-xs"
                                                                                    dropdownMatchSelectWidth={false}
                                                                                >
                                                                                    <Select.Option value="Pending"><Tag color="default" className="mr-0 font-bold text-[10px]">Pending</Tag></Select.Option>
                                                                                    <Select.Option value="In Progress"><Tag color="blue" className="mr-0 font-bold text-[10px]">In Progress</Tag></Select.Option>
                                                                                    <Select.Option value="Completed"><Tag color="green" className="mr-0 font-bold text-[10px]">Completed</Tag></Select.Option>
                                                                                    <Select.Option value="Closed"><Tag color="default" className="mr-0 font-bold text-[10px]">Closed</Tag></Select.Option>
                                                                                    <Select.Option value="Delayed"><Tag color="red" className="mr-0 font-bold text-[10px]">Delayed</Tag></Select.Option>
                                                                                    <Select.Option value="Canceled"><Tag color="orange" className="mr-0 font-bold text-[10px]">Canceled</Tag></Select.Option>
                                                                                    <Select.Option value="In Escrow"><Tag color="purple" className="mr-0 font-bold text-[10px]">In Escrow</Tag></Select.Option>
                                                                                    <Select.Option value="Released"><Tag color="gold" className="mr-0 font-bold text-[10px]">Released</Tag></Select.Option>
                                                                                </Select>
                                         </div>
                                                                        </Tooltip>
                                                                        {canManageMilestone(activeProject) && m.status !== 'Completed' && m.status !== 'Closed' && m.status !== 'Close' && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleMilestoneStatusChange(mIndex, 'Completed');
                                                                                }}
                                                                                disabled={updatingMilestoneIndex === mIndex}
                                                                                className="px-2.5 py-1 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all flex items-center gap-1 shadow-xs shrink-0 active:scale-95 disabled:opacity-50"
                                                                                title="Mark milestone completed"
                                                                            >
                                                                                <CheckCircleOutlined className="text-[11px]" />
                                                                                <span className="hidden sm:inline">Complete</span>
                                                                            </button>
                                                                        )}
                                                                        {canEditThisProject(activeProject) && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    openCreateTaskInDrawer(m._id || m.id || m.name || m.title);
                                                                                }}
                                                                                className="px-2.5 py-1 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all flex items-center gap-1 shadow-xs shrink-0 active:scale-95"
                                                                                title="Add task to this milestone"
                                                                            >
                                                                                <PlusOutlined className="text-[11px]" />
                                                                                <span>Add Task</span>
                                                                            </button>
                                                                        )}
                                                                        {canManageMilestone(activeProject) && (
                                                                            <>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        openEditMilestone(m);
                                                                                    }}
                                                                                    className="px-2 py-1 text-xs font-bold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:text-blue-600 hover:border-blue-300 rounded-lg transition-all flex items-center gap-1 shrink-0"
                                                                                    title="Edit Milestone"
                                                                                >
                                                                                    <EditOutlined className="text-[11px]" />
                                                                                    <span>Edit</span>
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleDeleteMilestone(m, mIndex);
                                                                                    }}
                                                                                    className="p-1.5 text-xs text-slate-500 hover:text-red-600 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-red-50 hover:border-red-200 dark:hover:bg-red-950/40 rounded-lg transition-all shrink-0"
                                                                                    title="Delete Milestone"
                                                                                >
                                                                                    <DeleteOutlined className="text-[12px]" />
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Milestone Child Tasks Table */}
                                                            {!isCollapsed && (
                                                                <div>
                                                                    {/* Milestone Deliverables Checklist (if any) */}
                                                                    {Array.isArray(m.deliverables) && m.deliverables.length > 0 && (
                                                                        <div className="px-4 py-2.5 bg-slate-50/70 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800 text-xs flex flex-wrap gap-2 items-center">
                                                                            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Deliverables:</span>
                                                                            {m.deliverables.map((del, dIdx) => (
                                                                                <span key={dIdx} className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-[11px] font-semibold border ${del.done || del.completed ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-800/60' : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:border-slate-700'}`}>
                                                                                    {del.done || del.completed ? '✓' : '○'} {del.title || del.name}
                                                                                    {del.url && <a href={del.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline ml-0.5">↗</a>}
                                                                                </span>
                                                                            ))}
                                         </div>
                                                                    )}
                                                                    {parentTasks.length === 0 ? (
                                                                        <div className="p-4 text-center text-xs text-slate-400 font-medium">
                                                                            No parent tasks directly assigned under this milestone.
                                         </div>
                                                                    ) : (
                                                                        <div>
                                                                            {/* Table Column Headers */}
                                                                            <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200/60 dark:border-slate-700/60 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                                                                <div className="col-span-7 flex items-center gap-2">
                                                                                    <span className="w-5 text-center">#</span>
                                                                                    <span>Task Name</span>
                                         </div>
                                                                                <div className="col-span-2">Assignee</div>
                                                                                <div className="col-span-2">Timeline</div>
                                                                                <div className="col-span-1 text-right">Priority</div>
                                         </div>

                                                                            {/* Parent Task Rows */}
                                                                            {parentTasks.map((parent, pIdx) => {
                                                                                const pKey = String(parent._id || parent.id);
                                                                                const subtasks = subtasksByParentId.get(pKey) || [];
                                                                                const isExpanded = expandedViewTaskIds.has(pKey) || (Boolean(taskSearch.trim()) && subtasks.length > 0);
                                                                                const isDone = parent.status === 'Completed' || parent.status === 'Closed' || parent.status === 'Close';
                                                                                const isInProg = parent.status === 'In Progress' || parent.status === 'Active';

                                                                                const assigneeObj = parent.assignedTo || parent.assignee;
                                                                                const assigneeName = typeof assigneeObj === 'object' && assigneeObj !== null
                                                                                    ? `${assigneeObj.name || ''} ${assigneeObj.surname || ''}`.trim()
                                                                                    : (employees.find((e) => String(e._id) === String(assigneeObj))?.name || (typeof assigneeObj === 'string' && assigneeObj ? assigneeObj : null));
                                                                                const assigneeAvatar = (typeof assigneeObj === 'object' && assigneeObj !== null) ? (assigneeObj.avatar || assigneeObj.photo) : employees.find((e) => String(e._id) === String(assigneeObj))?.avatar;
                                                                                const assigneeInitials = assigneeName?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase() || 'U';

                                                                                return (
                                                                                    <div key={pKey} className="border-b border-slate-100 dark:border-slate-800/80 last:border-b-0">
                                                                                        <div className="grid grid-cols-12 gap-2 px-4 py-3 hover:bg-slate-50/60 dark:hover:bg-slate-800/60 items-center transition-colors">
                                                                                            <div className="col-span-7 flex items-center gap-2 min-w-0">
                                                                                                {subtasks.length > 0 ? (
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        onClick={(e) => { e.stopPropagation(); toggleViewTaskExpand(pKey); }}
                                                                                                        className="w-5 h-5 flex items-center justify-center rounded text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-slate-700 transition-colors text-[10px] shrink-0"
                                                                                                        title={isExpanded ? 'Collapse subtasks' : 'Expand subtasks'}
                                                                                                    >
                                                                                                        {isExpanded ? <DownOutlined /> : <RightOutlined />}
                                                                                                    </button>
                                                                                                ) : (
                                                                                                    <span className="w-5 shrink-0 text-center text-xs font-semibold text-slate-400">{pIdx + 1}</span>
                                                                                                )}

                                                                                                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${isDone ? 'bg-emerald-500 text-white shadow-xs' : isInProg ? 'bg-blue-100 text-blue-600 border border-blue-400' : 'bg-slate-200 text-slate-500'}`}>
                                                                                                    {isDone ? '✓' : ''}
                                                                                                </span>

                                                                                                <span
                                                                                                    onClick={() => openTaskInDrawer(parent._id || parent.id)}
                                                                                                    className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-colors"
                                                                                                    title="Click to view task details in drawer"
                                                                                                >
                                                                                                    {parent.name || parent.title}
                                                                                                </span>

                                                                                                <div className="flex items-center gap-1.5 text-slate-400 text-[11px] shrink-0 ml-1">
                                                                                                    {(parent.comments?.length > 0 || parent.commentsCount > 0) && (
                                                                                                        <span className="flex items-center gap-0.5 text-slate-500">
                                                                                                            <CommentOutlined className="text-[10px]" /> {parent.comments?.length || parent.commentsCount}
                                                                                                        </span>
                                                                                                    )}
                                                                                                    {(parent.attachments?.length > 0 || parent.files?.length > 0) && (
                                                                                                        <span className="flex items-center gap-0.5 text-slate-500">
                                                                                                            <PaperClipOutlined className="text-[10px]" /> {parent.attachments?.length || parent.files?.length}
                                                                                                        </span>
                                                                                                    )}
                                                                                                    {subtasks.length > 0 && (
                                                                                                        <span
                                                                                                            onClick={() => toggleViewTaskExpand(pKey)}
                                                                                                            className="flex items-center gap-0.5 text-indigo-600 dark:text-indigo-400 font-bold bg-indigo-50 dark:bg-indigo-950/60 px-1.5 py-0.2 rounded text-[10px] cursor-pointer"
                                                                                                        >
                                                                                                            ↳ {subtasks.length}
                                                                                                        </span>
                                                                                                    )}
                                         </div>
                                         </div>

                                                                                            <div className="col-span-2 flex items-center gap-1.5 truncate">
                                                                                                {assigneeName ? (
                                                                                                    <>
                                                                                                        <Avatar size={20} src={assigneeAvatar} className="bg-blue-600 text-[10px] shrink-0">
                                                                                                            {assigneeInitials}
                                                                                                        </Avatar>
                                                                                                        <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate">
                                                                                                            {assigneeName}
                                                                                                        </span>
                                                                                                    </>
                                                                                                ) : (
                                                                                                    <span className="text-xs text-slate-400">Unassigned</span>
                                                                                                )}
                                         </div>

                                                                                            <div className="col-span-2 text-xs font-medium text-slate-600 dark:text-slate-400 truncate">
                                                                                                {formatTaskDate(parent)}
                                         </div>

                                                                                            <div className="col-span-1 text-right">
                                                                                                <Tag color={parent.priority === 'Critical' || parent.priority === 'High' ? 'error' : parent.priority === 'Medium' ? 'warning' : 'blue'} className="text-[10px] font-bold mr-0">
                                                                                                    {parent.priority || 'Medium'}
                                                                                                </Tag>
                                         </div>
                                         </div>

                                                                                        {/* Subtasks under this Parent Task */}
                                                                                        {isExpanded && subtasks.length > 0 && (
                                                                                            <div className="bg-slate-50/70 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-800">
                                                                                                {subtasks.map((st, sIdx) => {
                                                                                                    const stDone = st.status === 'Completed' || st.status === 'Closed' || st.status === 'Close';
                                                                                                    const stInProg = st.status === 'In Progress' || st.status === 'Active';
                                                                                                    const stAssigneeObj = st.assignedTo || st.assignee;
                                                                                                    const stAssigneeName = typeof stAssigneeObj === 'object' && stAssigneeObj !== null
                                                                                                        ? `${stAssigneeObj.name || ''} ${stAssigneeObj.surname || ''}`.trim()
                                                                                                        : (employees.find((e) => String(e._id) === String(stAssigneeObj))?.name || (typeof stAssigneeObj === 'string' && stAssigneeObj ? stAssigneeObj : null));
                                                                                                    const stAssigneeAvatar = (typeof stAssigneeObj === 'object' && stAssigneeObj !== null) ? (stAssigneeObj.avatar || stAssigneeObj.photo) : employees.find((e) => String(e._id) === String(stAssigneeObj))?.avatar;
                                                                                                    const stInitials = stAssigneeName?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase() || 'U';

                                                                                                    return (
                                                                                                        <div key={st._id || st.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 hover:bg-white dark:hover:bg-slate-800/80 items-center transition-colors border-b border-slate-100/70 dark:border-slate-800/40 last:border-b-0 pl-10">
                                                                                                            <div className="col-span-7 flex items-center gap-2 min-w-0">
                                                                                                                <span className="w-4 text-center text-[11px] font-semibold text-slate-400">{sIdx + 1}</span>
                                                                                                                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 ${stDone ? 'bg-emerald-500 text-white' : stInProg ? 'bg-blue-100 text-blue-600 border border-blue-400' : 'bg-slate-200 text-slate-500'}`}>
                                                                                                                    {stDone ? '✓' : ''}
                                                                                                                </span>
                                                                                                                <span
                                                                                                                    onClick={() => openTaskInDrawer(st._id || st.id)}
                                                                                                                    className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-colors"
                                                                                                                    title="Click to view subtask details in drawer"
                                                                                                                >
                                                                                                                    {st.name || st.title}
                                                                                                                </span>
                                         </div>
                                                                                                            <div className="col-span-2 flex items-center gap-1.5 truncate">
                                                                                                                {stAssigneeName ? (
                                                                                                                    <>
                                                                                                                        <Avatar size={18} src={stAssigneeAvatar} className="bg-amber-600 text-[9px] shrink-0">
                                                                                                                            {stInitials}
                                                                                                                        </Avatar>
                                                                                                                        <span className="text-xs font-medium text-slate-600 dark:text-slate-300 truncate">
                                                                                                                            {stAssigneeName}
                                                                                                                        </span>
                                                                                                                    </>
                                                                                                                ) : (
                                                                                                                    <span className="text-xs text-slate-400">—</span>
                                                                                                                )}
                                         </div>
                                                                                                            <div className="col-span-2 text-xs text-slate-500 dark:text-slate-400 truncate">
                                                                                                                {formatTaskDate(st)}
                                         </div>
                                                                                                            <div className="col-span-1 text-right">
                                                                                                                {st.priority && (
                                                                                                                    <Tag color={st.priority === 'Critical' || st.priority === 'High' ? 'error' : st.priority === 'Medium' ? 'warning' : 'blue'} className="text-[9px] font-semibold mr-0">
                                                                                                                        {st.priority}
                                                                                                                    </Tag>
                                                                                                                )}
                                         </div>
                                         </div>
                                                                                                    );
                                                                                                })}
                                         </div>
                                                                                        )}
                                         </div>
                                                                                );
                                                                            })}
                                         </div>
                                                                    )}
                                         </div>
                                                            )}
                                         </div>
                                                    );
                                                })}

                                                {/* Tasks Without Milestone */}
                                                {unassignedParentTasks.length > 0 && (() => {
                                                    const isCollapsed = collapsedMilestoneIds.has('unassigned');
                                                    return (
                                                        <div className="bg-white dark:bg-slate-850 rounded-2xl border border-slate-200 dark:border-slate-750 shadow-xs overflow-hidden">
                                                            <div
                                                                onClick={() => toggleMilestoneCollapse('unassigned')}
                                                                className="px-4 py-3 bg-slate-100/90 dark:bg-slate-800/90 hover:bg-slate-200/70 dark:hover:bg-slate-750 flex items-center justify-between cursor-pointer select-none transition-colors border-b border-slate-200/80 dark:border-slate-700/80"
                                                            >
                                                                <div className="flex items-center gap-2.5">
                                                                    <span className="text-slate-500 text-xs transition-transform">
                                                                        {isCollapsed ? <RightOutlined /> : <DownOutlined />}
                                                                    </span>
                                                                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                                                                        Other Tasks (Without Milestone)
                                                                    </span>
                                                                    <span className="text-xs font-semibold text-slate-400">
                                                                        ({unassignedParentTasks.length} {unassignedParentTasks.length === 1 ? 'task' : 'tasks'})
                                                                    </span>
                                                                </div>
                                                                {canEditThisProject(activeProject) && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            openCreateTaskInDrawer(null);
                                                                        }}
                                                                        className="px-2.5 py-1 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all flex items-center gap-1 shadow-xs shrink-0 active:scale-95"
                                                                        title="Add task without milestone"
                                                                    >
                                                                        <PlusOutlined className="text-[11px]" />
                                                                        <span>Add Task</span>
                                                                    </button>
                                                                )}
                                                            </div>

                                                            {!isCollapsed && (
                                                                <div>
                                                                    <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200/60 dark:border-slate-700/60 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                                                        <div className="col-span-7 flex items-center gap-2">
                                                                            <span className="w-5 text-center">#</span>
                                                                            <span>Task Name</span>
                                         </div>
                                                                        <div className="col-span-2">Assignee</div>
                                                                        <div className="col-span-2">Timeline</div>
                                                                        <div className="col-span-1 text-right">Priority</div>
                                         </div>

                                                                    {unassignedParentTasks.map((parent, pIdx) => {
                                                                        const pKey = String(parent._id || parent.id);
                                                                        const subtasks = subtasksByParentId.get(pKey) || [];
                                                                        const isExpanded = expandedViewTaskIds.has(pKey) || (Boolean(taskSearch.trim()) && subtasks.length > 0);
                                                                        const isDone = parent.status === 'Completed' || parent.status === 'Closed' || parent.status === 'Close';
                                                                        const isInProg = parent.status === 'In Progress' || parent.status === 'Active';

                                                                        const assigneeObj = parent.assignedTo || parent.assignee;
                                                                        const assigneeName = typeof assigneeObj === 'object' && assigneeObj !== null
                                                                            ? `${assigneeObj.name || ''} ${assigneeObj.surname || ''}`.trim()
                                                                            : (employees.find((e) => String(e._id) === String(assigneeObj))?.name || (typeof assigneeObj === 'string' && assigneeObj ? assigneeObj : null));
                                                                        const assigneeAvatar = (typeof assigneeObj === 'object' && assigneeObj !== null) ? (assigneeObj.avatar || assigneeObj.photo) : employees.find((e) => String(e._id) === String(assigneeObj))?.avatar;
                                                                        const assigneeInitials = assigneeName?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase() || 'U';

                                                                        return (
                                                                            <div key={pKey} className="border-b border-slate-100 dark:border-slate-800/80 last:border-b-0">
                                                                                <div className="grid grid-cols-12 gap-2 px-4 py-3 hover:bg-slate-50/60 dark:hover:bg-slate-800/60 items-center transition-colors">
                                                                                    <div className="col-span-7 flex items-center gap-2 min-w-0">
                                                                                        {subtasks.length > 0 ? (
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={(e) => { e.stopPropagation(); toggleViewTaskExpand(pKey); }}
                                                                                                className="w-5 h-5 flex items-center justify-center rounded text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-slate-700 transition-colors text-[10px] shrink-0"
                                                                                                title={isExpanded ? 'Collapse subtasks' : 'Expand subtasks'}
                                                                                            >
                                                                                                {isExpanded ? <DownOutlined /> : <RightOutlined />}
                                                                                            </button>
                                                                                        ) : (
                                                                                            <span className="w-5 shrink-0 text-center text-xs font-semibold text-slate-400">{pIdx + 1}</span>
                                                                                        )}

                                                                                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${isDone ? 'bg-emerald-500 text-white shadow-xs' : isInProg ? 'bg-blue-100 text-blue-600 border border-blue-400' : 'bg-slate-200 text-slate-500'}`}>
                                                                                            {isDone ? '✓' : ''}
                                                                                        </span>

                                                                                        <span
                                                                                            onClick={() => openTaskInDrawer(parent._id || parent.id)}
                                                                                            className="text-xs font-bold text-slate-800 dark:text-slate-100 truncate hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-colors"
                                                                                            title="Click to view task details in drawer"
                                                                                        >
                                                                                            {parent.name || parent.title}
                                                                                        </span>

                                                                                        <div className="flex items-center gap-1.5 text-slate-400 text-[11px] shrink-0 ml-1">
                                                                                            {(parent.comments?.length > 0 || parent.commentsCount > 0) && (
                                                                                                <span className="flex items-center gap-0.5 text-slate-500">
                                                                                                    <CommentOutlined className="text-[10px]" /> {parent.comments?.length || parent.commentsCount}
                                                                                                </span>
                                                                                            )}
                                                                                            {(parent.attachments?.length > 0 || parent.files?.length > 0) && (
                                                                                                <span className="flex items-center gap-0.5 text-slate-500">
                                                                                                    <PaperClipOutlined className="text-[10px]" /> {parent.attachments?.length || parent.files?.length}
                                                                                                </span>
                                                                                            )}
                                                                                            {subtasks.length > 0 && (
                                                                                                <span
                                                                                                    onClick={() => toggleViewTaskExpand(pKey)}
                                                                                                    className="flex items-center gap-0.5 text-indigo-600 dark:text-indigo-400 font-bold bg-indigo-50 dark:bg-indigo-950/60 px-1.5 py-0.2 rounded text-[10px] cursor-pointer"
                                                                                                >
                                                                                                    ↳ {subtasks.length}
                                                                                                </span>
                                                                                            )}
                                         </div>
                                         </div>

                                                                                    <div className="col-span-2 flex items-center gap-1.5 truncate">
                                                                                        {assigneeName ? (
                                                                                            <>
                                                                                                <Avatar size={20} src={assigneeAvatar} className="bg-blue-600 text-[10px] shrink-0">
                                                                                                    {assigneeInitials}
                                                                                                </Avatar>
                                                                                                <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate">
                                                                                                    {assigneeName}
                                                                                                </span>
                                                                                            </>
                                                                                        ) : (
                                                                                            <span className="text-xs text-slate-400">Unassigned</span>
                                                                                        )}
                                         </div>

                                                                                    <div className="col-span-2 text-xs font-medium text-slate-600 dark:text-slate-400 truncate">
                                                                                        {formatTaskDate(parent)}
                                         </div>

                                                                                    <div className="col-span-1 text-right">
                                                                                        <Tag color={parent.priority === 'Critical' || parent.priority === 'High' ? 'error' : parent.priority === 'Medium' ? 'warning' : 'blue'} className="text-[10px] font-bold mr-0">
                                                                                            {parent.priority || 'Medium'}
                                                                                        </Tag>
                                         </div>
                                         </div>

                                                                                {/* Subtasks */}
                                                                                {isExpanded && subtasks.length > 0 && (
                                                                                    <div className="bg-slate-50/70 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-800">
                                                                                        {subtasks.map((st, sIdx) => {
                                                                                            const stDone = st.status === 'Completed' || st.status === 'Closed' || st.status === 'Close';
                                                                                            const stInProg = st.status === 'In Progress' || st.status === 'Active';
                                                                                            const stAssigneeObj = st.assignedTo || st.assignee;
                                                                                            const stAssigneeName = typeof stAssigneeObj === 'object' && stAssigneeObj !== null
                                                                                                ? `${stAssigneeObj.name || ''} ${stAssigneeObj.surname || ''}`.trim()
                                                                                                : (employees.find((e) => String(e._id) === String(stAssigneeObj))?.name || (typeof stAssigneeObj === 'string' && stAssigneeObj ? stAssigneeObj : null));
                                                                                            const stAssigneeAvatar = (typeof stAssigneeObj === 'object' && stAssigneeObj !== null) ? (stAssigneeObj.avatar || stAssigneeObj.photo) : employees.find((e) => String(e._id) === String(stAssigneeObj))?.avatar;
                                                                                            const stInitials = stAssigneeName?.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase() || 'U';

                                                                                            return (
                                                                                                <div key={st._id || st.id} className="grid grid-cols-12 gap-2 px-4 py-2.5 hover:bg-white dark:hover:bg-slate-800/80 items-center transition-colors border-b border-slate-100/70 dark:border-slate-800/40 last:border-b-0 pl-10">
                                                                                                    <div className="col-span-7 flex items-center gap-2 min-w-0">
                                                                                                        <span className="w-4 text-center text-[11px] font-semibold text-slate-400">{sIdx + 1}</span>
                                                                                                        <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 ${stDone ? 'bg-emerald-500 text-white' : stInProg ? 'bg-blue-100 text-blue-600 border border-blue-400' : 'bg-slate-200 text-slate-500'}`}>
                                                                                                            {stDone ? '✓' : ''}
                                                                                                        </span>
                                                                                                        <span
                                                                                                            onClick={() => openTaskInDrawer(st._id || st.id)}
                                                                                                            className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-colors"
                                                                                                            title="Click to view subtask details in drawer"
                                                                                                        >
                                                                                                            {st.name || st.title}
                                                                                                        </span>
                                         </div>
                                                                                                    <div className="col-span-2 flex items-center gap-1.5 truncate">
                                                                                                        {stAssigneeName ? (
                                                                                                            <>
                                                                                                                <Avatar size={18} src={stAssigneeAvatar} className="bg-amber-600 text-[9px] shrink-0">
                                                                                                                    {stInitials}
                                                                                                                </Avatar>
                                                                                                                <span className="text-xs font-medium text-slate-600 dark:text-slate-300 truncate">
                                                                                                                    {stAssigneeName}
                                                                                                                </span>
                                                                                                            </>
                                                                                                        ) : (
                                                                                                            <span className="text-xs text-slate-400">—</span>
                                                                                                        )}
                                         </div>
                                                                                                    <div className="col-span-2 text-xs text-slate-500 dark:text-slate-400 truncate">
                                                                                                        {formatTaskDate(st)}
                                         </div>
                                                                                                    <div className="col-span-1 text-right">
                                                                                                        {st.priority && (
                                                                                                            <Tag color={st.priority === 'Critical' || st.priority === 'High' ? 'error' : st.priority === 'Medium' ? 'warning' : 'blue'} className="text-[9px] font-semibold mr-0">
                                                                                                                {st.priority}
                                                                                                            </Tag>
                                                                                                        )}
                                         </div>
                                         </div>
                                                                                            );
                                                                                        })}
                                         </div>
                                                                                )}
                                         </div>
                                                                        );
                                                                    })}
                                         </div>
                                                            )}
                                         </div>
                                                    );
                                                })()}
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* ── TREE VIEW RENDERING ── */}
                                {taskViewMode === 'tree' && (
                                    <div className="space-y-3">
                                        {paginatedTreeParents.length > 0 ? (
                                            paginatedTreeParents.map((parent) => {
                                                const pKey = String(parent._id || parent.id);
                                                const subtasks = subtasksByParentId.get(pKey) || [];
                                                const matchingSubtasks = subtasks.filter((st) => doesTaskMatchFilters(st));
                                                const visibleSubtasks = taskSearch.trim() ? matchingSubtasks : subtasks;
                                                const isExpanded = expandedViewTaskIds.has(pKey) || (Boolean(taskSearch.trim()) && matchingSubtasks.length > 0);

                                                const isDone = parent.status === 'Completed' || parent.status === 'Closed' || parent.status === 'Close';
                                                const isInProg = parent.status === 'In Progress' || parent.status === 'Active';

                                                const assigneeObj = parent.assignedTo || parent.assignee;
                                                const assigneeName = typeof assigneeObj === 'object' && assigneeObj !== null
                                                    ? `${assigneeObj.name || ''} ${assigneeObj.surname || ''}`.trim()
                                                    : (employees.find((e) => String(e._id) === String(assigneeObj))?.name || (typeof assigneeObj === 'string' && assigneeObj ? assigneeObj : null));

                                                return (
                                                    <div key={pKey} className="bg-white dark:bg-slate-800/90 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm overflow-hidden transition-all">
                                                        {/* Parent Row Header */}
                                                        <div className="p-3.5 flex flex-wrap items-center justify-between gap-3 hover:bg-slate-50/50 dark:hover:bg-slate-750 transition-colors">
                                                            <div className="flex items-center gap-2.5 flex-1 min-w-[260px]">
                                                                {subtasks.length > 0 ? (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => toggleViewTaskExpand(pKey)}
                                                                        className="w-6 h-6 flex items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-100 transition-colors shrink-0 text-xs"
                                                                        title={isExpanded ? 'Collapse subtasks' : 'Expand subtasks'}
                                                                    >
                                                                        {isExpanded ? <DownOutlined /> : <RightOutlined />}
                                                                    </button>
                                                                ) : (
                                                                    <div className="w-6 shrink-0" />
                                                                )}

                                                                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${isDone ? 'bg-emerald-500' : isInProg ? 'bg-blue-500' : 'bg-slate-300'}`} />

                                                                <div className="min-w-0 flex-1">
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        {parent.taskId && (
                                                                            <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                                                                {parent.taskId}
                                                                            </span>
                                                                        )}
                                                                        {parent.generationSource && parent.generationSource !== 'manual' && (() => {
                                                                            const srcConfig = {
                                                                                ai: { label: '⚡ AI', className: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/80 dark:text-amber-300 dark:border-amber-800' },
                                                                                cre: { label: '🏗 CRE', className: 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950/80 dark:text-blue-300 dark:border-blue-800' },
                                                                                asana: { label: '🔄 Asana', className: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/80 dark:text-purple-300 dark:border-purple-800' },
                                                                                system: { label: '⚙️ System', className: 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/80 dark:text-emerald-300 dark:border-emerald-800' },
                                                                            };
                                                                            const c = srcConfig[parent.generationSource];
                                                                            return c ? <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold border whitespace-nowrap ${c.className}`}>{c.label}</span> : null;
                                                                        })()}
                                                                        <p
                                                                            onClick={() => openTaskInDrawer(parent._id || parent.id)}
                                                                            className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-colors"
                                                                            title="Click to view task details"
                                                                        >
                                                                            {parent.name || parent.title}
                                                                        </p>
                                                                        {subtasks.length > 0 && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => toggleViewTaskExpand(pKey)}
                                                                                className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full transition-all ${isExpanded
                                                                                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-300'
                                                                                    : 'bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 dark:bg-slate-700 dark:text-slate-300'
                                                                                    }`}
                                                                            >
                                                                                ↳ {subtasks.length} Subtasks
                                                                                <span className="text-[9px]">{isExpanded ? '▲' : '▼'}</span>
                                                                            </button>
                                                                        )}
                                         </div>
                                                                    {parent.description && (
                                                                        <p className="text-xs text-slate-400 truncate max-w-xl mt-0.5">
                                                                            {parent.description.replace(/<[^>]*>?/gm, '')}
                                                                        </p>
                                                                    )}
                                         </div>
                                         </div>

                                                            <div className="flex items-center gap-2 flex-wrap shrink-0">
                                                                <Tag color={isDone ? 'green' : isInProg ? 'blue' : 'default'} className="text-xs font-semibold mr-0">
                                                                    {parent.status || 'Pending'}
                                                                </Tag>
                                                                <Tag color={parent.priority === 'Critical' ? 'error' : parent.priority === 'High' ? 'warning' : 'blue'} className="text-xs font-semibold mr-0">
                                                                    {parent.priority || 'Medium'}
                                                                </Tag>
                                                                {assigneeName ? (
                                                                    <span className="text-xs text-slate-700 dark:text-slate-200 font-semibold flex items-center gap-1.5 bg-slate-100 dark:bg-slate-700/60 px-2.5 py-1 rounded-lg">
                                                                        <TeamOutlined className="text-blue-600" /> {assigneeName}
                                                                    </span>
                                                                ) : (
                                                                    <Tag color="default" className="text-xs font-bold text-slate-400 border-dashed mr-0">
                                                                        Unassigned
                                                                    </Tag>
                                                                )}
                                                                <span className="font-mono text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700/60 px-2.5 py-1 rounded-lg">
                                                                    ⏱️ {parent.estimatedHours || parent.estimatedTime || 4}h
                                                                </span>
                                                                {parent.dueDate && (
                                                                    <span className="text-[11px] font-medium text-slate-400">
                                                                        Due {dayjs(parent.dueDate).format('MMM DD')}
                                                                    </span>
                                                                )}
                                         </div>
                                         </div>

                                                        {/* Expanded Subtasks Container */}
                                                        {isExpanded && visibleSubtasks.length > 0 && (
                                                            <div className="bg-slate-50/70 dark:bg-slate-900/40 p-3.5 border-t border-slate-100 dark:border-slate-800 space-y-2">
                                                                <div className="flex items-center justify-between mb-1.5 pl-6">
                                                                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                                                        Nested Subtasks ({visibleSubtasks.length})
                                                                    </span>
                                         </div>
                                                                <div className="space-y-1.5 pl-4 border-l-2 border-indigo-200 dark:border-indigo-900/60 ml-3">
                                                                    {visibleSubtasks.map((st) => {
                                                                        const stDone = st.status === 'Completed' || st.status === 'Closed' || st.status === 'Close';
                                                                        const stInProg = st.status === 'In Progress' || st.status === 'Active';
                                                                        const stAssignee = st.assignedTo || st.assignee;
                                                                        const stAssigneeName = typeof stAssignee === 'object' && stAssignee !== null
                                                                            ? `${stAssignee.name || ''} ${stAssignee.surname || ''}`.trim()
                                                                            : (employees.find((e) => String(e._id) === String(stAssignee))?.name || (typeof stAssignee === 'string' && stAssignee ? stAssignee : null));

                                                                        return (
                                                                            <div
                                                                                key={st._id || st.id}
                                                                                className="flex flex-wrap items-center justify-between bg-white dark:bg-slate-800 p-2.5 rounded-xl border border-slate-200/80 dark:border-slate-700/60 hover:border-indigo-300 dark:hover:border-indigo-500 transition-all gap-2"
                                                                            >
                                                                                <div className="flex items-center gap-2.5 flex-1 min-w-[200px]">
                                                                                    <span className="text-slate-400 text-xs font-bold">↳</span>
                                                                                    <span className={`w-2 h-2 rounded-full shrink-0 ${stDone ? 'bg-emerald-500' : stInProg ? 'bg-blue-500' : 'bg-slate-300'}`} />
                                                                                    {st.taskId && (
                                                                                        <span className="font-mono text-[9px] font-bold px-1 rounded bg-slate-100 dark:bg-slate-700 text-slate-500">
                                                                                            {st.taskId}
                                                                                        </span>
                                                                                    )}
                                                                                    <span
                                                                                        onClick={() => openTaskInDrawer(st._id || st.id)}
                                                                                        className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer transition-colors"
                                                                                        title="Click to view subtask details"
                                                                                    >
                                                                                        {st.name || st.title}
                                                                                    </span>
                                         </div>

                                                                                <div className="flex items-center gap-2 flex-wrap shrink-0 text-xs">
                                                                                    <Tag color={stDone ? 'green' : stInProg ? 'blue' : 'default'} className="text-[11px] font-semibold mr-0">
                                                                                        {st.status || 'Pending'}
                                                                                    </Tag>
                                                                                    <Tag color={st.priority === 'Critical' ? 'error' : st.priority === 'High' ? 'warning' : 'blue'} className="text-[11px] font-semibold mr-0">
                                                                                        {st.priority || 'Medium'}
                                                                                    </Tag>
                                                                                    {stAssigneeName ? (
                                                                                        <span className="text-[11px] text-slate-600 dark:text-slate-300 font-medium flex items-center gap-1 bg-slate-100 dark:bg-slate-700/60 px-2 py-0.5 rounded-md">
                                                                                            <TeamOutlined className="text-blue-500 text-[10px]" /> {stAssigneeName}
                                                                                        </span>
                                                                                    ) : (
                                                                                        <span className="text-[11px] text-slate-400 italic">Unassigned</span>
                                                                                    )}
                                                                                    <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400">
                                                                                        ⏱️ {st.estimatedHours || st.estimatedTime || 4}h
                                                                                    </span>
                                                                                    {st.dueDate && (
                                                                                        <span className="text-[10px] text-slate-400">
                                                                                            {dayjs(st.dueDate).format('MMM DD')}
                                                                                        </span>
                                                                                    )}
                                         </div>
                                         </div>
                                                                        );
                                                                    })}
                                         </div>
                                         </div>
                                                        )}
                                         </div>
                                                );
                                            })
                                        ) : (
                                            <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl space-y-2">
                                                <CheckSquareOutlined className="text-4xl text-slate-300" />
                                                <p className="text-sm font-bold text-slate-600 dark:text-slate-400">No parent tasks match your filter</p>
                                                <p className="text-xs text-slate-400">Try adjusting your search keyword or clearing assignment filters.</p>
                                         </div>
                                        )}

                                        {/* Orphan subtasks if any */}
                                        {orphanSubtasks.length > 0 && (
                                            <div className="bg-amber-50/50 dark:bg-amber-950/20 p-4 rounded-2xl border border-amber-200/80 dark:border-amber-900/40 space-y-2 mt-4">
                                                <p className="text-xs font-bold text-amber-800 dark:text-amber-300 uppercase tracking-wider">
                                                    Direct Subtasks ({orphanSubtasks.length})
                                                </p>
                                                <div className="space-y-1.5">
                                                    {orphanSubtasks.map((st) => (
                                                        <div key={st._id || st.id} className="flex items-center justify-between bg-white dark:bg-slate-800 p-2.5 rounded-xl border border-amber-200/60 dark:border-amber-800/40 text-xs">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-amber-500 font-bold">↳</span>
                                                                <span
                                                                    onClick={() => openTaskInDrawer(st._id || st.id)}
                                                                    className="font-semibold text-slate-800 dark:text-slate-200 hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer transition-colors"
                                                                    title="Click to view subtask details"
                                                                >
                                                                    {st.name || st.title}
                                                                </span>
                                         </div>
                                                            <Tag color="blue">{st.status || 'Backlog'}</Tag>
                                         </div>
                                                    ))}
                                         </div>
                                         </div>
                                        )}
                                    </div>
                                )}

                                {/* ── FLAT VIEW RENDERING ── */}
                                {taskViewMode === 'flat' && (
                                    <div className="space-y-2.5">
                                        {paginatedFlatTasks.length > 0 ? (
                                            paginatedFlatTasks.map((t) => {
                                                const assigneeObj = t.assignedTo || t.assignee;
                                                const assigneeName = typeof assigneeObj === 'object' && assigneeObj !== null
                                                    ? `${assigneeObj.name || ''} ${assigneeObj.surname || ''}`.trim()
                                                    : (employees.find((e) => String(e._id) === String(assigneeObj))?.name || (typeof assigneeObj === 'string' && assigneeObj ? assigneeObj : null));

                                                const isDone = t.status === 'Completed' || t.status === 'Closed' || t.status === 'Close';
                                                const isInProg = t.status === 'In Progress' || t.status === 'Active';
                                                const parentTask = t.parentId ? taskById.get(String(typeof t.parentId === 'object' ? (t.parentId._id || t.parentId.id) : t.parentId)) : null;

                                                return (
                                                    <div
                                                        key={t._id || t.id}
                                                        className="flex flex-wrap items-center justify-between bg-white dark:bg-slate-800 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700/80 shadow-sm hover:border-blue-400 dark:hover:border-blue-500 transition-all gap-3"
                                                    >
                                                        <div className="flex items-center gap-3 flex-1 min-w-[240px]">
                                                            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${isDone ? 'bg-emerald-500' : isInProg ? 'bg-blue-500' : 'bg-slate-300'}`} />
                                                            <div className="min-w-0">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    {t.taskId && (
                                                                        <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                                                            {t.taskId}
                                                                        </span>
                                                                    )}
                                                                    {t.generationSource && t.generationSource !== 'manual' && (() => {
                                                                        const srcConfig = {
                                                                            ai: { label: '⚡ AI', className: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/80 dark:text-amber-300 dark:border-amber-800' },
                                                                            cre: { label: '🏗 CRE', className: 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950/80 dark:text-blue-300 dark:border-blue-800' },
                                                                            asana: { label: '🔄 Asana', className: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/80 dark:text-purple-300 dark:border-purple-800' },
                                                                            system: { label: '⚙️ System', className: 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/80 dark:text-emerald-300 dark:border-emerald-800' },
                                                                        };
                                                                        const c = srcConfig[t.generationSource];
                                                                        return c ? <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold border whitespace-nowrap ${c.className}`}>{c.label}</span> : null;
                                                                    })()}
                                                                    <p
                                                                        onClick={() => openTaskInDrawer(t._id || t.id)}
                                                                        className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-colors"
                                                                        title="Click to view task details"
                                                                    >
                                                                        {t.name || t.title}
                                                                    </p>
                                                                    {t.parentId && (
                                                                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/60 px-2 py-0.5 rounded-md">
                                                                            ↳ Subtask of: {parentTask ? (parentTask.name || parentTask.title) : 'Parent Task'}
                                                                        </span>
                                                                    )}
                                         </div>
                                                                {t.description && (
                                                                    <p className="text-xs text-slate-400 truncate max-w-lg mt-0.5">
                                                                        {t.description.replace(/<[^>]*>?/gm, '')}
                                                                    </p>
                                                                )}
                                         </div>
                                         </div>

                                                        <div className="flex items-center gap-2 flex-wrap shrink-0">
                                                            <Tag color={isDone ? 'green' : isInProg ? 'blue' : 'default'} className="text-xs font-semibold mr-0">
                                                                {t.status || 'Pending'}
                                                            </Tag>
                                                            <Tag color={t.priority === 'Critical' ? 'error' : t.priority === 'High' ? 'warning' : 'blue'} className="text-xs font-semibold mr-0">
                                                                {t.priority || 'Medium'}
                                                            </Tag>
                                                            {assigneeName ? (
                                                                <span className="text-xs text-slate-700 dark:text-slate-200 font-semibold flex items-center gap-1.5 bg-slate-100 dark:bg-slate-700/60 px-2.5 py-1 rounded-lg">
                                                                    <TeamOutlined className="text-blue-600" /> {assigneeName}
                                                                </span>
                                                            ) : (
                                                                <Tag color="default" className="text-xs font-bold text-slate-400 border-dashed mr-0">
                                                                    Unassigned
                                                                </Tag>
                                                            )}
                                                            <span className="font-mono text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700/60 px-2.5 py-1 rounded-lg">
                                                                ⏱️ {t.estimatedHours || t.estimatedTime || 4}h
                                                            </span>
                                                            {t.dueDate && (
                                                                <span className="text-[11px] font-medium text-slate-400">
                                                                    Due {dayjs(t.dueDate).format('MMM DD')}
                                                                </span>
                                                            )}
                                         </div>
                                         </div>
                                                );
                                            })
                                        ) : (
                                            <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl space-y-2">
                                                <CheckSquareOutlined className="text-4xl text-slate-300" />
                                                <p className="text-sm font-bold text-slate-600 dark:text-slate-400">No tasks match your filter criteria</p>
                                                <p className="text-xs text-slate-400">Try adjusting your search keyword or clearing assignment filters.</p>
                                         </div>
                                        )}
                                    </div>
                                )}

                                {/* Pagination Controls */}
                                {totalTaskPages > 1 && (
                                    <div className="flex items-center justify-between pt-2 px-1 text-xs font-semibold text-slate-500">
                                        <span>
                                            Showing page {taskPage} of {totalTaskPages} ({isTree ? `${filteredTreeParents.length} parent tasks` : `${filteredFlatTasks.length} total tasks`})
                                        </span>
                                        <div className="flex items-center gap-1">
                                            <button
                                                type="button"
                                                onClick={() => setTaskPage((p) => Math.max(1, p - 1))}
                                                disabled={taskPage <= 1}
                                                className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 disabled:opacity-30 hover:bg-slate-200 transition-colors"
                                            >
                                                Prev
                                            </button>
                                            <span className="px-2 font-bold text-[#003263] dark:text-[#a7c8ff]">
                                                {taskPage} / {totalTaskPages}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setTaskPage((p) => Math.min(totalTaskPages, p + 1))}
                                                disabled={taskPage >= totalTaskPages}
                                                className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 disabled:opacity-30 hover:bg-slate-200 transition-colors"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}


                        {/* ════════════════ TAB 3: DOCUMENTS ════════════════ */}
                        {activeTab === 'documents' && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between pb-2 border-b border-slate-200/80 dark:border-slate-800">
                                    <span className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                                        Project Documents ({documents.length})
                                    </span>
                                    {canEditThisProject(activeProject) && (
                                        <div>
                                            <input
                                                type="file"
                                                ref={docInputRef}
                                                onChange={handleUploadDocument}
                                                className="hidden"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => docInputRef.current?.click()}
                                                disabled={uploadingDoc}
                                                className="px-3.5 py-1.5 text-xs font-bold text-white bg-[#003263] hover:bg-[#022447] rounded-xl transition-all shadow-xs flex items-center gap-1.5 active:scale-95 disabled:opacity-50"
                                            >
                                                {uploadingDoc ? <LoadingOutlined /> : <UploadOutlined />}
                                                Upload Document
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {documents.length > 0 ? (
                                    <div className="space-y-2">
                                        {documents.map((doc, i) => (
                                            <div key={doc._id || doc.id || i} className="bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center justify-between hover:bg-slate-100/60 dark:hover:bg-slate-850 transition-colors">
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <FilePdfOutlined className="text-2xl text-blue-600 shrink-0" />
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{doc.fileName}</p>
                                                        <p className="text-xs text-slate-400 mt-0.5">
                                                            {doc.fileSize ? (typeof doc.fileSize === 'number' ? `${(doc.fileSize / 1024).toFixed(1)} KB` : doc.fileSize) : 'Document'} • Uploaded {dayjs(doc.uploadedAt).format('MMM DD, YYYY')}
                                                            {doc.uploadedBy && ` by ${typeof doc.uploadedBy === 'object' ? (doc.uploadedBy.name || 'User') : doc.uploadedBy}`}
                                                        </p>
                                         </div>
                                         </div>

                                                <div className="flex items-center gap-2 shrink-0 ml-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleViewDocument(doc, i)}
                                                        className="px-2.5 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-bold text-slate-700 dark:text-slate-200 flex items-center gap-1 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                                                        title="View document"
                                                    >
                                                        <EyeOutlined /> View
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDownloadDocument(doc, i)}
                                                        className="px-2.5 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-bold text-slate-700 dark:text-slate-200 flex items-center gap-1 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                                                        title="Download document"
                                                    >
                                                        <DownloadOutlined /> Download
                                                    </button>
                                                    {canEditThisProject(activeProject) && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteDocument(i)}
                                                            className="p-1.5 rounded-lg text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors text-xs"
                                                            title="Delete document"
                                                        >
                                                            <DeleteOutlined />
                                                        </button>
                                                    )}
                                         </div>
                                         </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
                                        <FilePdfOutlined className="text-4xl text-slate-300 mb-2" />
                                        <p className="text-sm font-bold text-slate-500">No Documents Attached</p>
                                        <p className="text-xs text-slate-400 mt-1">Upload project specifications, contracts, and PDFs directly to this project.</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ════════════════ TAB 4: ASSETS ════════════════ */}
                        {activeTab === 'assets' && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between pb-2 border-b border-slate-200/80 dark:border-slate-800">
                                    <span className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                                        Project Assets ({assets.length})
                                    </span>
                                    {canEditThisProject(activeProject) && (
                                        <div>
                                            <input
                                                type="file"
                                                ref={assetInputRef}
                                                accept="image/*"
                                                onChange={handleUploadAsset}
                                                className="hidden"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => assetInputRef.current?.click()}
                                                disabled={uploadingAsset}
                                                className="px-3.5 py-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-all shadow-xs flex items-center gap-1.5 active:scale-95 disabled:opacity-50"
                                            >
                                                {uploadingAsset ? <LoadingOutlined /> : <UploadOutlined />}
                                                Upload Asset
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {assets.length > 0 ? (
                                    assetViewMode === 'grid' ? (
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            {assets.map((asset, i) => (
                                                <div key={asset._id || asset.id || i} className="bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col items-center text-center space-y-2 group relative">
                                                    {(asset.publicUrl || asset.fileUrl || asset.url) ? (
                                                        <img
                                                            src={asset.publicUrl || asset.fileUrl || asset.url}
                                                            alt={asset.fileName}
                                                            className="w-16 h-16 object-cover rounded-lg border border-slate-200 dark:border-slate-700"
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                        />
                                                    ) : (
                                                        <PictureOutlined className="text-3xl text-indigo-500" />
                                                    )}
                                                    <p className="text-xs font-bold truncate w-full" title={asset.fileName}>{asset.fileName}</p>
                                                    <Tag className="text-[10px] mr-0">{asset.fileType || 'Asset'}</Tag>
                                                    <div className="flex items-center gap-1 pt-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleViewAsset(asset, i)}
                                                            className="p-1 text-slate-500 hover:text-blue-600 transition-colors text-xs cursor-pointer bg-transparent border-0"
                                                            title="View asset"
                                                        >
                                                            <EyeOutlined />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDownloadAsset(asset, i)}
                                                            className="p-1 text-slate-500 hover:text-emerald-600 transition-colors text-xs cursor-pointer bg-transparent border-0"
                                                            title="Download asset"
                                                        >
                                                            <DownloadOutlined />
                                                        </button>
                                                        {canEditThisProject(activeProject) && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDeleteAsset(i)}
                                                                className="p-1 text-slate-400 hover:text-rose-600 transition-colors text-xs"
                                                                title="Delete asset"
                                                            >
                                                                <DeleteOutlined />
                                                            </button>
                                                        )}
                                         </div>
                                         </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {assets.map((asset, i) => (
                                                <div key={asset._id || asset.id || i} className="bg-slate-50 dark:bg-slate-900 p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs font-semibold">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <PictureOutlined className="text-indigo-500 text-lg shrink-0" />
                                                        <span className="truncate">{asset.fileName}</span>
                                                        <Tag className="mr-0 ml-1 text-[10px]">{asset.fileType || 'Asset'}</Tag>
                                         </div>
                                                    <div className="flex items-center gap-2 shrink-0 ml-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleViewAsset(asset, i)}
                                                            className="p-1 text-slate-500 hover:text-blue-600 transition-colors cursor-pointer bg-transparent border-0"
                                                            title="View"
                                                        >
                                                            <EyeOutlined />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDownloadAsset(asset, i)}
                                                            className="p-1 text-slate-500 hover:text-emerald-600 transition-colors cursor-pointer bg-transparent border-0"
                                                            title="Download"
                                                        >
                                                            <DownloadOutlined />
                                                        </button>
                                                        {canEditThisProject(activeProject) && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDeleteAsset(i)}
                                                                className="p-1 text-slate-400 hover:text-rose-600 transition-colors"
                                                                title="Delete asset"
                                                            >
                                                                <DeleteOutlined />
                                                            </button>
                                                        )}
                                         </div>
                                         </div>
                                            ))}
                                        </div>
                                    )
                                ) : (
                                    <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
                                        <PictureOutlined className="text-4xl text-slate-300 mb-2" />
                                        <p className="text-sm font-bold text-slate-500">No Project Assets Attached</p>
                                        <p className="text-xs text-slate-400 mt-1">Brand files, logos, SVG, design references, and media will appear here.</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ════════════════ TAB 5: ACTIVITY & HISTORY LOG ════════════════ */}
                        {activeTab === 'history' && (
                            <div className="space-y-4">
                                {(() => {
                                    const logs = getActivityLogs();
                                    if (logs.length > 0) {
                                        return (
                                            <div className="bg-slate-50 dark:bg-slate-900/60 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4">
                                                <h3 className="text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Project Timeline &amp; Audit Log</h3>
                                                <div className="space-y-4 relative before:absolute before:inset-0 before:left-4 before:w-0.5 before:bg-slate-200 dark:before:bg-slate-800">
                                                    {logs.map((log, idx) => (
                                                        <div key={idx} className="relative flex items-start gap-4 pl-8">
                                                            <span className={`absolute left-2.5 top-1.5 w-3 h-3 rounded-full ring-4 ring-white dark:ring-slate-900 ${log.type === 'cre_sync' ? 'bg-emerald-500' :
                                                                log.type === 'milestone' ? 'bg-blue-500' :
                                                                    log.type === 'status_change' ? 'bg-purple-500' : 'bg-slate-400'
                                                                }`} />
                                                            <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 flex-1 space-y-1 shadow-sm">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-xs font-bold text-[#003263] dark:text-[#a7c8ff] uppercase tracking-wider">{log.action}</span>
                                                                    <span className="text-[11px] font-semibold text-slate-400">{log.timestamp ? dayjs(log.timestamp).format('MMM DD, YYYY • hh:mm A') : 'Recently'}</span>
                                         </div>
                                                                <p className="text-xs text-slate-700 dark:text-slate-300 font-medium">{log.description}</p>

                                                                {/* ── Attachment Link Pill ── */}
                                                                {(() => {
                                                                    const match = log.description?.match(/"([^"]+)"/);
                                                                    const fileName = log.fileName || (match ? match[1] : null);
                                                                    let fileUrl = log.fileUrl;
                                                                    if (!fileUrl && fileName) {
                                                                        const doc = (activeProject.documents || []).find((d) => (d.fileName || d.name) === fileName);
                                                                        if (doc?.publicUrl || doc?.fileUrl || doc?.url) fileUrl = doc.publicUrl || doc.fileUrl || doc.url;
                                                                        else {
                                                                            const asset = (activeProject.assets || []).find((a) => (a.fileName || a.name) === fileName);
                                                                            if (asset?.publicUrl || asset?.url) fileUrl = asset.publicUrl || asset.url;
                                                                        }
                                                                    }
                                                                    if (fileUrl) {
                                                                        return (
                                                                            <div className="pt-2">
                                                                                <a
                                                                                    href={fileUrl}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-lg text-xs font-bold hover:bg-blue-100 transition-all shadow-sm"
                                                                                >
                                                                                    <FilePdfOutlined /> View / Download "{fileName || 'Attachment'}" <EyeOutlined className="text-xs" />
                                                                                </a>
                                         </div>
                                                                        );
                                                                    }
                                                                    return null;
                                                                })()}

                                                                <p className="text-[11px] text-slate-400 pt-1">Logged by: <span className="font-bold text-slate-600 dark:text-slate-300">{log.performedBy || 'System'}</span></p>
                                         </div>
                                         </div>
                                                    ))}
                                         </div>
                                         </div>
                                        );
                                    }
                                    return (
                                        <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
                                            <UnorderedListOutlined className="text-4xl text-slate-300 mb-2" />
                                            <p className="text-sm font-bold text-slate-500">No Activity Logs Yet</p>
                                            <p className="text-xs text-slate-400 mt-1">Status changes, milestone reviews, and client confirmations will automatically be logged here.</p>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                    </div>

                    {/* ── Modal Footer Bar ── */}
                    <div className="bg-slate-50 dark:bg-slate-900 p-5 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-2.5 text-sm font-bold text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
                        >
                            Close
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                const projId = project?._id || activeProject?._id;
                                if (projId) {
                                    window.open(`/project-dashboard?projectId=${projId}&tab=overview`, '_blank');
                                } else {
                                    onOpenProject(project);
                                }
                            }}
                            className="px-6 py-2.5 text-sm font-bold text-white rounded-xl shadow-md hover:opacity-90 active:scale-95 transition-all flex items-center gap-2"
                            style={{ background: 'linear-gradient(90deg, #03488A 0%, #006591 100%)' }}
                        >
                            <ExportOutlined /> View Full Detail
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                open={questionnaireAnswersOpen}
                zIndex={1400}
                onCancel={() => setQuestionnaireAnswersOpen(false)}
                footer={null}
                centered
                width={760}
                title={
                    <div>
                        <div className="text-base font-bold text-slate-900 dark:text-white">
                            {activeProject.linkedQuestionnaire?.title || activeProject.linkedQuestionnaire?.name || activeProject.questionnaireTemplateName || 'Project Questionnaire'}
                        </div>
                        <div className="text-xs font-normal text-slate-500 dark:text-slate-400 mt-1">
                            {activeProject.linkedQuestionnaire?.status || 'Submitted'}
                            {activeProject.linkedQuestionnaire?.submittedDate || activeProject.linkedQuestionnaire?.submittedAt
                                ? ` • ${dayjs(activeProject.linkedQuestionnaire.submittedDate || activeProject.linkedQuestionnaire.submittedAt).format('DD MMM YYYY')}`
                                : ''}
                        </div>
                    </div>
                }
            >
                <div className="max-h-[65vh] overflow-y-auto pr-1 space-y-3">
                    {questionnaireResponses.length > 0 ? (
                        questionnaireResponses.map((response, idx) => (
                            <div
                                key={`${response.order || idx}-${response.question || idx}`}
                                className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/70 p-4"
                            >
                                <div className="flex items-start gap-3">
                                    <span className="w-7 h-7 shrink-0 rounded-full bg-blue-100 text-blue-700 border border-blue-200 flex items-center justify-center text-xs font-bold">
                                        {response.order || idx + 1}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-bold text-sm text-slate-800 dark:text-slate-100 leading-snug">
                                            {response.question || 'Untitled question'}
                                        </div>
                                        {response.answerType && (
                                            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mt-1">
                                                {response.answerType}
                                         </div>
                                        )}
                                        <div className="mt-3 rounded-lg bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap">
                                            {formatQuestionnaireAnswer(response.answer)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-sm text-slate-500 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                            No questionnaire answers were saved for this project.
                        </div>
                    )}
                </div>
            </Modal>

            <MilestoneCompletionModal
                open={completionModalOpen}
                onClose={() => {
                    setCompletionModalOpen(false);
                    setCompletingMilestone(null);
                    setCompletingMilestoneIndex(null);
                }}
                onConfirm={handleConfirmMilestoneCompletion}
                loading={completingLoading}
                milestone={completingMilestone || {}}
            />

            {/* ── Add / Edit Smart Goal Modal ── */}
            <Modal
                title={editingGoalId ? 'Edit Smart Goal' : 'Add Smart Goal'}
                open={showGoalModal}
                onCancel={() => setShowGoalModal(false)}
                onOk={handleSaveGoal}
                okText={editingGoalId ? 'Update Goal' : 'Add Goal'}
                centered
                zIndex={1450}
                width={520}
            >
                <div className="space-y-4 pt-3">
                    <div>
                        <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-1">
                            Goal Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            placeholder="e.g., Achieve 99.9% API Uptime & Load Compliance"
                            value={goalForm.name}
                            onChange={(e) => setGoalForm((prev) => ({ ...prev, name: e.target.value }))}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-1">
                            Description
                        </label>
                        <Input.TextArea
                            rows={2}
                            placeholder="Describe the objective and expected business impact..."
                            value={goalForm.description}
                            onChange={(e) => setGoalForm((prev) => ({ ...prev, description: e.target.value }))}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-1">
                            Success Criteria (Measurable KPI)
                        </label>
                        <Input
                            placeholder="e.g., Response time < 200ms across all endpoints"
                            value={goalForm.successCriteria}
                            onChange={(e) => setGoalForm((prev) => ({ ...prev, successCriteria: e.target.value }))}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-1">
                                Target Date
                            </label>
                            <DatePicker
                                className="w-full"
                                value={goalForm.targetDate ? (dayjs.isDayjs(goalForm.targetDate) ? goalForm.targetDate : dayjs(goalForm.targetDate)) : null}
                                onChange={(date) => setGoalForm((prev) => ({ ...prev, targetDate: date }))}
                                format="MMM DD, YYYY"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-1">
                                Priority
                            </label>
                            <Select
                                className="w-full"
                                value={goalForm.priority || 'High'}
                                onChange={(val) => setGoalForm((prev) => ({ ...prev, priority: val }))}
                            >
                                <Select.Option value="Critical">Critical</Select.Option>
                                <Select.Option value="High">High</Select.Option>
                                <Select.Option value="Medium">Medium</Select.Option>
                                <Select.Option value="Low">Low</Select.Option>
                            </Select>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* ── AI Smart Goals Conflict Modal (Replace vs Append) ── */}
            <Modal
                title="Existing Smart Goals Found"
                open={Boolean(aiConflictModal?.open ?? aiConflictModal)}
                onCancel={() => setAiConflictModal(false)}
                footer={null}
                centered
                zIndex={1460}
                width={440}
            >
                <div className="space-y-4 pt-2">
                    <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                        This project already has <strong>{getCurrentGoalsList(editForm).length}</strong> smart goal(s). How would you like AI to proceed?
                    </p>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={() => setAiConflictModal(false)}
                            className="px-3.5 py-2 text-xs font-bold text-slate-500 hover:text-slate-800 rounded-xl transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => triggerSmartGoalsGeneration('append')}
                            className="px-4 py-2 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                        >
                            Append New Goals
                        </button>
                        <button
                            type="button"
                            onClick={() => triggerSmartGoalsGeneration('replace')}
                            className="px-4 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors shadow-sm"
                        >
                            Replace Existing
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Add / Edit Milestone Modal ── */}
            <Modal
                title={editingMilestone ? 'Edit Milestone' : 'Create New Milestone'}
                open={showMilestoneModal}
                onCancel={() => {
                    setShowMilestoneModal(false);
                    setEditingMilestone(null);
                }}
                onOk={handleSaveMilestone}
                confirmLoading={savingMilestone}
                okText={editingMilestone ? 'Save Changes' : 'Create Milestone'}
                centered
                zIndex={1450}
                width={600}
            >
                <div className="space-y-4 pt-3 max-h-[70vh] overflow-y-auto pr-1">
                    <div>
                        <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-1">
                            Milestone Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                            placeholder="e.g., Phase 1: Architecture & Core Setup"
                            value={milestoneForm.name}
                            onChange={(e) => setMilestoneForm((prev) => ({ ...prev, name: e.target.value }))}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-1">
                            Description
                        </label>
                        <Input.TextArea
                            rows={2}
                            placeholder="Describe what this milestone covers..."
                            value={milestoneForm.description}
                            onChange={(e) => setMilestoneForm((prev) => ({ ...prev, description: e.target.value }))}
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-1">
                                Start Date
                            </label>
                            <DatePicker
                                className="w-full"
                                value={milestoneForm.startDate ? (dayjs.isDayjs(milestoneForm.startDate) ? milestoneForm.startDate : dayjs(milestoneForm.startDate)) : null}
                                onChange={(date) => setMilestoneForm((prev) => ({ ...prev, startDate: date }))}
                                format="MMM DD, YYYY"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-1">
                                Due Date <span className="text-red-500">*</span>
                            </label>
                            <DatePicker
                                className="w-full"
                                value={milestoneForm.dueDate ? (dayjs.isDayjs(milestoneForm.dueDate) ? milestoneForm.dueDate : dayjs(milestoneForm.dueDate)) : null}
                                onChange={(date) => setMilestoneForm((prev) => ({ ...prev, dueDate: date }))}
                                format="MMM DD, YYYY"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-1">
                                Milestone Owner
                            </label>
                            <Select
                                showSearch
                                allowClear
                                optionFilterProp="children"
                                placeholder="Assign Owner..."
                                className="w-full"
                                value={milestoneForm.owner || undefined}
                                onChange={(val) => setMilestoneForm((prev) => ({ ...prev, owner: val }))}
                            >
                                {employees.map((emp) => (
                                    <Select.Option key={emp._id || emp.id} value={emp._id || emp.id}>
                                        {formatName(emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`.trim())}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-1">
                                Status
                            </label>
                            <Select
                                className="w-full"
                                value={milestoneForm.status || 'Pending'}
                                onChange={(val) => setMilestoneForm((prev) => ({ ...prev, status: val }))}
                            >
                                <Select.Option value="Pending">Pending</Select.Option>
                                <Select.Option value="In Progress">In Progress</Select.Option>
                                <Select.Option value="Completed">Completed</Select.Option>
                                <Select.Option value="Delayed">Delayed</Select.Option>
                                <Select.Option value="Closed">Closed</Select.Option>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-1">
                            Completion Criteria
                        </label>
                        <Input
                            placeholder="e.g., All unit tests passing and client sign-off received"
                            value={milestoneForm.completionCriteria}
                            onChange={(e) => setMilestoneForm((prev) => ({ ...prev, completionCriteria: e.target.value }))}
                        />
                    </div>
                    {/* Deliverables Checklist */}
                    <div className="space-y-2 pt-1 border-t border-slate-200 dark:border-slate-800">
                        <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">
                            Deliverables Checklist ({(milestoneForm.deliverables || []).length})
                        </label>
                        <div className="flex gap-2">
                            <Input
                                placeholder="Add a deliverable item..."
                                value={newDeliverableTitle}
                                onChange={(e) => setNewDeliverableTitle(e.target.value)}
                                onPressEnter={(e) => {
                                    e.preventDefault();
                                    handleAddDeliverableToForm();
                                }}
                            />
                            <button
                                type="button"
                                onClick={handleAddDeliverableToForm}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg shrink-0 transition-colors"
                            >
                                + Add
                            </button>
                        </div>
                        {(milestoneForm.deliverables || []).length > 0 && (
                            <div className="space-y-1.5 max-h-40 overflow-y-auto pt-1">
                                {(milestoneForm.deliverables || []).map((del, dIdx) => (
                                    <div
                                        key={dIdx}
                                        className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-xs"
                                    >
                                        <div
                                            onClick={() => handleToggleDeliverableInForm(dIdx)}
                                            className="flex items-center gap-2 cursor-pointer select-none flex-1 min-w-0"
                                        >
                                            <span className={`w-4 h-4 rounded flex items-center justify-center border text-[10px] font-bold ${del.done || del.completed ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 dark:border-slate-600'}`}>
                                                {del.done || del.completed ? <CheckOutlined /> : null}
                                            </span>
                                            <span className={`truncate ${del.done || del.completed ? 'line-through text-slate-400' : 'text-slate-700 dark:text-slate-200 font-medium'}`}>
                                                {del.title || del.name}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveDeliverableFromForm(dIdx)}
                                            className="text-slate-400 hover:text-red-500 transition-colors ml-2"
                                        >
                                            <DeleteOutlined />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </Modal>

            <TaskDetailDrawer
                taskId={drawerTaskId}
                mode={drawerMode}
                initialMilestoneId={drawerMilestoneId}
                selectedProject={activeProject}
                projects={[activeProject]}
                open={isDrawerOpen}
                onClose={closeTaskDrawer}
                zIndex={1300}
                currentAdminId={currentAdmin?._id}
                currentAdmin={currentAdmin}
                employees={employees}
                onTaskUpdated={handleTaskUpdatedFromDrawer}
                onSuccess={handleTaskCreatedFromDrawer}
                workspaceTasks={fetchedTasks.length > 0 ? fetchedTasks : tasks}
            />

            <ShareProjectModal
                open={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                project={activeProject}
                onProjectUpdated={(updatedData) => {
                    setFullProject((prev) => ({ ...(prev || activeProject), ...updatedData }));
                    onRefresh?.();
                }}
            />
        </>
    );
}
